"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Dropdown, Modal, Segmented, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  CalendarOutlined,
  CameraOutlined,
  DeleteOutlined,
  HeartFilled,
  LoadingOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  SafetyOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  UserOutlined as UserIconOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@/places/context/ThemeContext";
import { axiosAuth, axiosGateway } from "@/api/axios";
import { USERS_PATHS, POSTS_PATHS } from "@/api/endpoints";

import SubProfileForm from "../SubProfileForm/SubProfileForm";
import useSousProfiles from "@/sous-profile/hooks/useSousProfiles";
import useUserInformation from "@/Profile/hooks/useUserInformation";
import useRelatives from "@/Profile/hooks/useRelatives";
import { selectEffectiveUser } from "@/sous-profile/redux/selectors/sousProfileSelectors";
import { fetchUsers } from "@/Profile/redux/thunks/userThunks";
import { deleteRelative } from "@/Profile/redux/thunks/relativesThunks";
import { sendEmancipationEmail } from "@/sous-profile/redux/thunks/sousProfileThunks";
import { generateGeneticRiskReport } from "@/sous-profile/api/genetiqueAiApi";

import "./familyTree.css";

/* ══════════════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════════════ */


/* ══════════════════════════════════════════════════════════
   CUSTOM SVG ICON — Generate AI button
══════════════════════════════════════════════════════════ */

const GenerateAiIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2L14.3 9.7L22 12L14.3 14.3L12 22L9.7 14.3L2 12L9.7 9.7L12 2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 3.5L19.2 5.8L21.5 6.5L19.2 7.2L18.5 9.5L17.8 7.2L15.5 6.5L17.8 5.8L18.5 3.5Z"
      fill="currentColor"
    />
  </svg>
);

const BabyAvatarIcon = ({ size = 30 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className="ft-new-baby-svg"
  >
    <path
      d="M16.2 6.4C17.2 4.1 20.6 3.8 21.8 5.9C22.9 8 20.9 10.2 18.7 9.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.7 12.1C11.8 8.9 14.6 7.2 17.8 7.5C21.2 7.9 23.7 10.7 23.7 14.1V17.1C23.7 21.7 20.4 25.1 16 25.1C11.6 25.1 8.3 21.7 8.3 17.1V14.4C8.3 13.5 9 12.7 9.9 12.5L10.7 12.1Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.5 14.6C6.9 14.6 5.8 15.7 5.8 17.2C5.8 18.8 7 19.9 8.7 19.9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M23.5 14.6C25.1 14.6 26.2 15.7 26.2 17.2C26.2 18.8 25 19.9 23.3 19.9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="13.2" cy="16.5" r="1" fill="currentColor" />
    <circle cx="18.8" cy="16.5" r="1" fill="currentColor" />
    <path
      d="M13.1 20.3C14.8 21.7 17.2 21.7 18.9 20.3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);


const ACTION_DEFS = {
  relative: {
    key: "relative",
    icon: <TeamOutlined />,
    label: "Ajouter comme proche",
  },
  "sub-profile": {
    key: "sub-profile",
    icon: <UserAddOutlined />,
    label: "Créer un sous-profil",
  },
};

// Les positions sont gardées pour préserver toute la logique existante.
// Le nouveau design n'utilise plus directement ces coordonnées pour le rendu.
const W = 900;
const ME_CX = 450;
const ME_CY = 290;
const SPOUSE_CX = 650;
const PARENT_CY = 80;
const PARENT1_CX = 310;
const PARENT2_CX = 590;
const SIBLING_CY = ME_CY;
const CHILD_CY = 530;
const R_DEFAULT = 36;
const CARD_PADDING_X = 50;
const CHILD_MIN_GAP = 40;
const SIBLING_MIN_GAP = 40;
const R_ME = 44;

const NODES = {
  parent1: { cx: PARENT1_CX, cy: PARENT_CY },
  parent2: { cx: PARENT2_CX, cy: PARENT_CY },
  me: { cx: ME_CX, cy: ME_CY },
  spouse: { cx: SPOUSE_CX, cy: ME_CY },
};

/* ══════════════════════════════════════════════════════════
   HELPERS — relatives
══════════════════════════════════════════════════════════ */

const getRelativeUserId = (rel) =>
  rel?.relativeId || rel?.relativeUser?.id || rel?.linkedUserId || null;

const getUserId = (user) =>
  user?.id ||
  user?._id ||
  user?.userId ||
  user?.uuid ||
  user?.sub ||
  user?.patientId ||
  "";

const getApiErrorMessage = (error) => {
  const payload = error?.response?.data;
  if (typeof payload === "string") return payload;
  if (payload?.detail)
    return typeof payload.detail === "string"
      ? payload.detail
      : JSON.stringify(payload.detail);
  if (payload?.message)
    return typeof payload.message === "string"
      ? payload.message
      : JSON.stringify(payload.message);
  return error?.message || "Analyse IA indisponible pour le moment.";
};

const readSerializedMedicalField = (chunk, key) => {
  const match = chunk.match(
    new RegExp(`['"]${key}['"]\\s*:\\s*['"]([^'"]*)['"]`, "i")
  );
  return match?.[1]?.trim() || "";
};

const cleanSerializedMedicalText = (value = "") => {
  const text = String(value);
  if (
    !/[{[]/.test(text) ||
    !/['"]id['"]|['"]condition['"]|['"]diagnosis['"]/.test(text)
  ) {
    return text;
  }

  return text.replace(/\{[^{}]*\}/g, (chunk) => {
    const condition = readSerializedMedicalField(chunk, "condition");
    const diagnosis = readSerializedMedicalField(chunk, "diagnosis");
    const normalized = condition
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    if (
      !condition ||
      ["condition", "diagnosis", "annee", "year"].includes(normalized)
    ) {
      return "";
    }

    return diagnosis &&
      !["annee", "année", "diagnosis", "year"].includes(
        diagnosis.toLowerCase()
      )
      ? `${condition} (${diagnosis})`
      : condition;
  });
};

const stripTechnicalIds = (value = "") =>
  cleanSerializedMedicalText(value)
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "membre"
    )
    .replace(/\b[a-f0-9]{20,}\b/gi, "membre")
    .replace(/['"]id['"]\s*:\s*['"]?[^,'"}\]]+['"]?,?/gi, "")
    .replace(/['"]isNew['"]\s*:\s*(true|false|True|False),?/gi, "")
    .replace(/[\[\]{}]/g, "")
    .replace(/,\s*,/g, ",")
    .replace(/:\s*,/g, ":")
    .replace(/\s+/g, " ")
    .trim();

const relationLabel = (relation = "") => {
  const labels = {
    SELF: "Vous",
    CHILD: "Enfant",
    SON: "Fils",
    DAUGHTER: "Fille",
    PARENT: "Parent",
    FATHER: "Père",
    MOTHER: "Mère",
    BROTHER: "Frère",
    SISTER: "Sœur",
    SPOUSE: "Conjoint(e)",
    HUSBAND: "Mari",
    WIFE: "Épouse",
    SPOUSE_FATHER: "Parent du conjoint",
    SPOUSE_MOTHER: "Parent du conjoint",
    SPOUSE_PARENT: "Parent du conjoint",
  };

  return labels[String(relation).toUpperCase()] || "Membre";
};

const normalizeRelationType = (relation = "") =>
  String(relation)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const normalizeGender = (gender = "") => {
  const value = String(gender).trim().toLowerCase();

  if (["male", "m", "man", "homme"].includes(value)) {
    return "male";
  }

  if (["female", "f", "woman", "femme"].includes(value)) {
    return "female";
  }

  return "unknown";
};

const getSpouseRelationConfig = (user) => {
  const gender = normalizeGender(user?.gender || user?.sex);

  if (gender === "male") {
    return {
      label: "Wife",
      relationType: "wife",
      allowedRelations: ["wife"],
    };
  }

  if (gender === "female") {
    return {
      label: "Husband",
      relationType: "husband",
      allowedRelations: ["husband"],
    };
  }

  return {
    label: "Spouse",
    relationType: "spouse",
    allowedRelations: ["husband", "wife"],
  };
};

const riskLabel = (level = "") => {
  const labels = {
    HIGH: "Risque eleve",
    MODERATE: "Risque modere",
    LOW: "Risque faible",
    UNKNOWN: "A preciser",
  };
  return labels[String(level).toUpperCase()] || "A preciser";
};

const riskTagColor = (level = "") => {
  const normalized = String(level).toUpperCase();
  if (normalized === "HIGH") return "red";
  if (normalized === "MODERATE") return "orange";
  if (normalized === "LOW") return "green";
  return "blue";
};

const normalizeEmailValue = (email = "") =>
  String(email).trim().toLowerCase();

const isRelativeAccepted = (rel) => {
  const status = (rel.status || rel.state || rel.relationshipStatus || "")
    .toString()
    .toLowerCase();
  return (
    status === "pending" ||
    status === "accepted" ||
    status === "active" ||
    [rel.accepted, rel.isAccepted, rel.confirmed].some(Boolean)
  );
};

const extractLinkedUser = (rel) => rel?.relativeUser || rel?.user || null;

/* ══════════════════════════════════════════════════════════
   HELPERS — layout state (kept for existing data logic)
══════════════════════════════════════════════════════════ */

function layoutChildrenX(count) {
  if (count === 0) return [];
  const left = CARD_PADDING_X + R_DEFAULT;
  const right = W - CARD_PADDING_X - R_DEFAULT;
  if (count === 1) return [ME_CX];
  const available = right - left;
  const gapIdeal = available / (count - 1);
  const gap = Math.min(gapIdeal, R_DEFAULT * 2 + CHILD_MIN_GAP);
  const xs = Array.from({ length: count }, (_, i) => left + i * gap);
  const mid = (xs[0] + xs[xs.length - 1]) / 2;
  const shift = ME_CX - mid;
  const clamped = Math.max(
    left - xs[0],
    Math.min(right - xs[xs.length - 1], shift)
  );
  return xs.map((x) => Math.max(left, Math.min(right, x + clamped)));
}

function layoutSiblingsX(count) {
  const addBtnX = ME_CX - R_ME - 100;
  const spacing = R_DEFAULT * 2 + SIBLING_MIN_GAP;
  const rightBound = addBtnX - spacing;
  const leftBound = CARD_PADDING_X + R_DEFAULT;
  if (count === 0) return [];
  if (count === 1) return [(leftBound + rightBound) / 2];
  const available = rightBound - leftBound;
  if (available <= 0)
    return Array.from({ length: count }, () => leftBound);
  const gap = available / (count - 1);
  return Array.from({ length: count }, (_, i) => leftBound + i * gap);
}

/* ══════════════════════════════════════════════════════════
   HELPERS — member state
══════════════════════════════════════════════════════════ */

const INITIAL_MEMBERS = {
  /*
   * Business rule:
   * - top left  = Mother
   * - top right = Father
   */
  parent1: {
    id: "parent1",
    label: "Mother",
    role: "parent",
    relationType: "mother",
    allowedRelations: ["mother"],
    position: NODES.parent1,
  },

  parent2: {
    id: "parent2",
    label: "Father",
    role: "parent",
    relationType: "father",
    allowedRelations: ["father"],
    position: NODES.parent2,
  },

  me: {
    id: "me",
    label: "Moi",
    role: "me",
    position: NODES.me,
  },

  /*
   * The spouse slot is specialized later from the authenticated
   * user's gender:
   * male   -> wife only
   * female -> husband only
   */
  spouse: {
    id: "spouse",
    label: "Spouse",
    role: "spouse",
    relationType: "spouse",
    allowedRelations: ["husband", "wife"],
    optional: true,
    position: NODES.spouse,
  },
};

function buildFilledSlot(base, user) {
  const firstName = user?.firstName || user?.firstname || "";
  const lastName = user?.lastName || user?.lastname || "";
  return {
    ...base,
    filled: true,
    firstName,
    lastName,
    avatarUrl:
      user?.avatar || user?.avatarUrl || user?.profilePictureUrl || null,
    gender: user?.gender || "not_specified",
    label: `${firstName} ${lastName}`.trim() || base.label,
  };
}

const arraysEqual = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((v, i) => v === b[i]);

const normalizeSearch = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

function sameChildCore(a, b) {
  if (!a || !b) return false;
  return (
    a.role === b.role &&
    a.label === b.label &&
    a.filled === b.filled &&
    a.spId === b.spId &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.gender === b.gender &&
    a.avatarUrl === b.avatarUrl
  );
}

/* ══════════════════════════════════════════════════════════
   NEW DESIGN — cards (Figma-aligned)
══════════════════════════════════════════════════════════ */

const getMemberName = (member) => {
  if (!member) return "";
  if (member.firstName) {
    return `${member.firstName} ${member.lastName || ""}`.trim();
  }
  return member.label || "";
};

const getMemberVariant = (member) => {
  if (!member) return "other";
  if (member.role === "me") return "me";
  if (member.role === "parent") return "parent";
  if (member.role === "spouse") return "spouse";
  if (member.role === "child") return "child";
  return "other";
};

const getMemberBadge = (member) => {
  if (!member) return "";
  if (member.role === "me") return "Your profile";

  // When the tree is re-rooted around an acting sub-profile,
  // show the relationship from the active profile's point of view.
  if (member.relationshipLabel) return member.relationshipLabel;

  if (member.spId) return "Sous-profile";
  if (member.filled) return "Family member";
  return "Sous-profile";
};

function FamilyMemberCard({
  member,
  onClick,
  onMoreClick,
  vertical = false,
  compact = false,
}) {
  const { t } = useTranslation("sousProfile");
  if (!member) return null;

  const variant = getMemberVariant(member);
  const isMe = member.role === "me";
  const isParent = member.role === "parent";
  const filled = isMe || member.filled;
  const relationLabel = {
    mother: t("familyTree.detail.roles.parent"),
    father: t("familyTree.detail.roles.parent"),
    husband: t("familyTree.ui.spouse"),
    wife: t("familyTree.ui.spouse"),
    brother: t("familyTree.ui.sibling"),
    sister: t("familyTree.ui.sibling"),
    son: t("familyTree.ui.child"),
    daughter: t("familyTree.ui.child"),
  }[member.relationType];

  const name =
    !filled && isParent
      ? relationLabel || member.label || t("familyTree.ui.parent")
      : getMemberName(member) ||
        (member.role === "spouse"
          ? member.label || t("familyTree.ui.spouse")
          : member.role === "child"
          ? t("familyTree.ui.addMember")
          : member.role === "sibling"
          ? t("familyTree.ui.sibling")
          : t("familyTree.ui.addMember"));

  const badge = member.role === "me"
    ? t("familyTree.ui.yourProfile")
    : member.spId
    ? t("familyTree.ui.subprofile")
    : member.filled
    ? t("familyTree.ui.familyMember")
    : t("familyTree.ui.subprofile");

  return (
    <button
      type="button"
      className={[
        "ft-new-member",
        `ft-new-member--${variant}`,
        isMe ? "ft-new-member--me-center" : "",
        vertical ? "ft-new-member--vertical" : "",
        compact ? "ft-new-member--compact" : "",
        !filled ? "ft-new-member--empty" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onClick?.(member)}
    >
      <div className="ft-new-avatar-wrap">
        <Avatar
          src={member.avatarUrl || undefined}
          icon={
            !member.avatarUrl
              ? variant === "child"
                ? <BabyAvatarIcon />
                : <UserOutlined />
              : undefined
          }
          className={`ft-new-avatar ft-new-avatar--${variant}`}
        />
      </div>

      <div className="ft-new-member-info">
        <div className="ft-new-member-name">{name}</div>

        <span className={`ft-new-badge ft-new-badge--${variant}`}>
          {badge}
        </span>
      </div>

      {isParent && !filled && (
        <span className="ft-new-parent-plus" aria-hidden="true">
          <PlusOutlined />
        </span>
      )}

      {filled && !isMe && member.relativeRelationId && onMoreClick && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "delete",
                danger: true,
                icon: <DeleteOutlined />,
                label: t("familyTree.ui.deleteRelationship"),
              },
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "delete") onMoreClick(member);
            },
          }}
        >
          <span
            className="ft-new-more"
            onClick={(event) => event.stopPropagation()}
            role="button"
            tabIndex={0}
            aria-label={t("familyTree.ui.actionsFor", { name })}
          >
            <MoreOutlined />
          </span>
        </Dropdown>
      )}
    </button>
  );
}

function AddMemberCard({
  label = "Add Member",
  badge = "Sous-profile",
  variant = "other",
  onClick,
}) {
  const { t } = useTranslation("sousProfile");

  return (
    <button
      type="button"
      className={`ft-new-add-card ft-new-add-card--${variant}`}
      onClick={onClick}
    >
      <span className={`ft-new-add-icon ft-new-add-icon--${variant}`}>
        <PlusOutlined />
      </span>

      <span className="ft-new-add-title">{label || t("familyTree.ui.addMember")}</span>

      <span className={`ft-new-badge ft-new-badge--${variant}`}>
        {badge}
      </span>
    </button>
  );
}


function MiniSiblingCard({ member, onClick, onMoreClick }) {
  const { t } = useTranslation("sousProfile");
  if (!member) return null;

  const variant = getMemberVariant(member);
  const name = getMemberName(member) || t("familyTree.ui.unknownUser");
  const relationType = normalizeRelationType(member.relationType || "");

  const badge =
    variant === "child"
      ? member.stackBadge ||
        member.relationshipLabel ||
        (member.spId
          ? t("familyTree.ui.subprofile")
          : member.filled
          ? t("familyTree.ui.familyMember")
          : t("familyTree.ui.subprofile"))
      : relationType === "brother" || relationType === "sister"
      ? t("familyTree.ui.sibling")
      : member.stackBadge ||
        member.relationshipLabel ||
        t("familyTree.ui.sibling");

  return (
    <button
      type="button"
      className={[
        "ft-sibling-mini-card",
        `ft-sibling-mini-card--${variant}`,
      ].join(" ")}
      onClick={() => onClick?.(member)}
      title={name}
    >
      <div className="ft-sibling-mini-card__avatar-wrap">
        <Avatar
          src={member.avatarUrl || undefined}
          icon={
            !member.avatarUrl
              ? variant === "child"
                ? <BabyAvatarIcon size={18} />
                : <UserOutlined />
              : undefined
          }
          className={`ft-sibling-mini-card__avatar ft-sibling-mini-card__avatar--${variant}`}
        />
      </div>

      <div className="ft-sibling-mini-card__body">
        <span className="ft-sibling-mini-card__name">{name}</span>

        <span
          className={[
            "ft-sibling-mini-card__badge",
            variant === "child"
              ? "ft-sibling-mini-card__badge--child"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {badge}
        </span>
      </div>

      {member.relativeRelationId && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "delete",
                danger: true,
                icon: <DeleteOutlined />,
                label: t("familyTree.ui.deleteRelationship"),
              },
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "delete") onMoreClick?.(member);
            },
          }}
        >
          <span
            className="ft-sibling-mini-card__more"
            onClick={(event) => event.stopPropagation()}
            role="button"
            tabIndex={0}
            aria-label={t("familyTree.ui.actionsFor", { name })}
          >
            <MoreOutlined />
          </span>
        </Dropdown>
      )}
    </button>
  );
}

function FamilyLegend() {
  const { t } = useTranslation("sousProfile");
  const items = [
    { type: "me", label: t("familyTree.ui.yourProfile") },
    { type: "parent", label: t("familyTree.ui.parent") },
    { type: "other", label: t("familyTree.ui.familyMember") },
    { type: "spouse", label: t("familyTree.ui.spouse") },
    { type: "child", label: t("familyTree.ui.child") },
  ];

  return (
    <div className="ft-new-legend">
      {items.map((item) => (
        <div className="ft-new-legend-item" key={item.type}>
          <span
            className={`ft-new-legend-color ft-new-legend-color--${item.type}`}
          />
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function FamilyTreeBoard({
  members = {},
  childrenIds = [],
  siblingIds = [],
  onNodeClick,
  onSiblingMore,
  onAddSibling,
  onAddChild,
  compact = false,
}) {
  const { t } = useTranslation("sousProfile");
  const parent1 = members.parent1;
  const parent2 = members.parent2;
  const me = members.me;
  const spouse = members.spouse;

  const children = childrenIds.map((id) => members[id]).filter(Boolean);
  const siblings = siblingIds.map((id) => members[id]).filter(Boolean);
  const hasChildren = children.length > 0;

  // 1–3 children: current horizontal layout.
  // 4+ children: compact stack, exactly like Brother / Sister.
  const shouldStackChildren = children.length > 3;

  return (
    <div className={`ft-new-root${compact ? " ft-new-root--compact" : ""}`}>
      <div className="ft-new-scroll">
        <div className="ft-new-tree">
          <div className="ft-new-tree-scene">
            {/* Parents */}
            <div className="ft-new-parents">
              <div className="ft-new-parent">
                <FamilyMemberCard
                  member={parent1}
                  onClick={onNodeClick}
                  onMoreClick={onSiblingMore}
                />
              </div>

              <div className="ft-new-parent">
                <FamilyMemberCard
                  member={parent2}
                  onClick={onNodeClick}
                  onMoreClick={onSiblingMore}
                />
              </div>

              <span className="ft-new-parent-horizontal" />
              <span className="ft-new-parent-down" />
            </div>

            {/* Add member — current user — spouse */}
            <div className="ft-new-middle">
              <span className="ft-new-middle-left-line" />
              <span className="ft-new-middle-right-line" />

              <div className="ft-new-other-zone">
                {siblings.length > 0 ? (
                  <div
                    className={[
                      "ft-new-sibling-cluster",
                      siblings.length === 1
                        ? "ft-new-sibling-cluster--single"
                        : "ft-new-sibling-cluster--multiple",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      className="ft-new-sibling-add-trigger"
                      onClick={onAddSibling}
                      aria-label={t("familyTree.ui.addMember")}
                    >
                      <PlusOutlined />
                    </button>

                    <div className="ft-new-sibling-list">
                      {siblings.map((member, index) => (
                        <div
                          key={member.id}
                          className="ft-new-sibling-stack-item"
                          style={{
                            "--ft-sibling-index": index + 1,
                            "--ft-sibling-z": siblings.length - index,
                          }}
                        >
                          <MiniSiblingCard
                            member={member}
                            onClick={onNodeClick}
                            onMoreClick={onSiblingMore}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <AddMemberCard
                    label={t("familyTree.ui.addMember")}
                    badge={t("familyTree.ui.subprofile")}
                    variant="other"
                    onClick={onAddSibling}
                  />
                )}
              </div>

              <div className="ft-new-current">
                <FamilyMemberCard member={me} onClick={onNodeClick} />
                <span className="ft-new-current-down" />
              </div>

              <div className="ft-new-spouse">
                <span className="ft-new-heart">
                  <HeartFilled className="heart-icon" />
                </span>

                {spouse?.filled ? (
                  <FamilyMemberCard
                    member={spouse}
                    onClick={onNodeClick}
                    onMoreClick={onSiblingMore}
                    vertical
                  />
                ) : (
                  <AddMemberCard
                    label={spouse?.label || t("familyTree.ui.spouse")}
                    badge={t("familyTree.ui.subprofile")}
                    variant="spouse"
                    onClick={() => onNodeClick?.(spouse)}
                  />
                )}
              </div>
            </div>

            {/* Children */}
            <div
              className={[
                "ft-new-children",
                hasChildren
                  ? "ft-new-children--branched"
                  : "ft-new-children--empty",
                shouldStackChildren ? "ft-new-children--stacked" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {shouldStackChildren ? (
                <div className="ft-new-child-stack-zone">
                  <span className="ft-new-child-stack-parent-line" />

                  <div className="ft-new-child-stack-cluster ft-new-child-stack-cluster--multiple">
                    <button
                      type="button"
                      className="ft-new-child-stack-add-trigger"
                      onClick={onAddChild}
                      aria-label={t("familyTree.ui.addMember")}
                    >
                      <PlusOutlined />
                    </button>

                    <div className="ft-new-child-stack-list">
                      {children.map((member, index) => (
                        <div
                          key={member.id}
                          className="ft-new-child-stack-item"
                          style={{
                            "--ft-child-index": index + 1,
                            "--ft-child-z": children.length - index,
                          }}
                        >
                          <MiniSiblingCard
                            member={member}
                            onClick={onNodeClick}
                            onMoreClick={onSiblingMore}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : hasChildren ? (
                <>
                  <span className="ft-new-child-main-line" />

                  <div className="ft-new-child-list">
                    {children.map((member) => (
                      <div className="ft-new-child" key={member.id}>
                        <span className="ft-new-child-line" />

                        <FamilyMemberCard
                          member={member}
                          onClick={onNodeClick}
                          onMoreClick={onSiblingMore}
                        />
                      </div>
                    ))}

                    <div className="ft-new-child ft-new-child--add">
                      <span className="ft-new-child-line" />

                      <AddMemberCard
                        label={t("familyTree.ui.addMember")}
                        badge={t("familyTree.ui.subprofile")}
                        variant="other"
                        onClick={onAddChild}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="ft-new-single-child-add">
                  <span className="ft-new-single-child-line" />

                  <AddMemberCard
                    label={t("familyTree.ui.addMember")}
                    badge={t("familyTree.ui.subprofile")}
                    variant="other"
                    onClick={onAddChild}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <FamilyLegend />
    </div>
  );
}


/* ══════════════════════════════════════════════════════════
   FamilyTree — main component
══════════════════════════════════════════════════════════ */

export default function FamilyTree({
  onAddRelativeFromTree,
  onAiStart,
  onAiGenerated,
  onAiError,
  aiPanelOpen = false,
  canSaveReport = false,
  onSaveReport,
}) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("sousProfile");

  /*
   * UI direction follows the selected language, but the mathematical
   * geometry of the family tree stays LTR. CSS below re-enables RTL
   * only inside labels/cards so Arabic text is displayed naturally.
   */
  const isRTL =
    i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";

  const [members, setMembers] = useState({ ...INITIAL_MEMBERS });
  const [childrenIds, setChildrenIds] = useState([]);
  const [siblingIds, setSiblingIds] = useState([]);
  const [subProfileModal, setSubProfileModal] = useState({
    open: false,
    forMember: null,
  });
  const [genericModal, setGenericModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");

  const [guardiansBySpId, setGuardiansBySpId] = useState({});
  const [addGuardianOpen, setAddGuardianOpen] = useState(false);
  const [newGuardianName, setNewGuardianName] = useState("");
  const [newGuardianEmail, setNewGuardianEmail] = useState("");
  const [newGuardianRole, setNewGuardianRole] = useState("secondary");
  const [selectedGuardianUser, setSelectedGuardianUser] = useState(null);

  const [spChoiceModal, setSpChoiceModal] = useState({
    open: false,
    forMember: null,
  });

  const [primaryGuardianPopup, setPrimaryGuardianPopup] = useState({
    open: false,
    member: null,
  });

  const [grantIndependenceModal, setGrantIndependenceModal] = useState({
    open: false,
    member: null,
  });
  const [grantConfirmText, setGrantConfirmText] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [editableEmail, setEditableEmail] = useState("");
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    member: null,
  });
  const [isDeletingSubprofile, setIsDeletingSubprofile] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState("");

  const { isDark } = useTheme();

  const {
    items: availableSousProfiles,
    current: activeSousProfile,
    actingSession,
    isActingAsSubprofile,
    ownerId,
    refetch,
    handleSwitchProfile,
    handleRestoreProfile,
    handleDeleteSubprofile,
    handleUpdateSubprofile,
    handleGrantAccess,
    handleGrantAccessByEmail,
    handleRevokeAccess,
  } = useSousProfiles({ autoFetch: true });

  const sousProfiles = useMemo(
    () =>
      isActingAsSubprofile
        ? activeSousProfile
          ? [activeSousProfile]
          : []
        : availableSousProfiles,
    [activeSousProfile, availableSousProfiles, isActingAsSubprofile]
  );

  const [uploadingAvatarFor, setUploadingAvatarFor] = useState(null);

  const authUser = useSelector(selectEffectiveUser);
  const users = useSelector((s) => Object.values(s.user?.usersById || {}));
  const { avatarSrc } = useUserInformation(authUser, null);

  useRelatives({ autofetch: true });
  const relativesRaw = useSelector((s) => s.relatives?.data || []);

  useEffect(() => {
    if (!users.length) {
      dispatch(fetchUsers());
    }
  }, [dispatch, users.length]);

  useEffect(() => {
    if (!grantIndependenceModal.open) {
      setEmailError("");
      setSubmitError("");
      setGrantConfirmText("");
      setEditableEmail("");
      return;
    }

    const m = grantIndependenceModal.member;
    const spKey = String(m?.spId || m?.id);
    const guardiansData = guardiansBySpId[spKey] || [];
    const primaryGuardian = guardiansData[0];
    setEditableEmail(primaryGuardian?.email || "");
  }, [grantIndependenceModal.open, grantIndependenceModal.member, guardiansBySpId]);

  useEffect(() => {
    if (!editableEmail) {
      setEmailError("");
      return;
    }

    const currentUserEmail = authUser?.email || "";
    if (
      currentUserEmail &&
      editableEmail.toLowerCase() === currentUserEmail.toLowerCase()
    ) {
      setEmailError(
        t("familyTree.grantIndependence.errorOwnEmail") ||
          "Vous ne pouvez pas envoyer à votre propre email"
      );
      return;
    }

    const checkEmail = async () => {
      try {
        const response = await axiosAuth.get(
          `${USERS_PATHS.SEARCH}?email=${encodeURIComponent(editableEmail)}`,
          { withCredentials: true }
        );

        const userExists =
          response?.data?.data && response.data.data.length > 0;

        if (userExists) {
          setEmailError(
            t("familyTree.grantIndependence.emailAlreadyExists", {
              email: editableEmail,
            })
          );
        } else {
          setEmailError("");
        }
      } catch (error) {
        console.error("Erreur vérification email:", error);
        setEmailError("");
      }
    };

    checkEmail();
  }, [editableEmail, t, authUser?.email]);

  const guardianSuggestions = useMemo(() => {
    const nameQuery = normalizeSearch(newGuardianName);
    if (!nameQuery) return [];

    return users
      .filter((u) => {
        const fullName =
          `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
          u.fullName ||
          u.email ||
          "";
        const normalizedName = normalizeSearch(fullName);
        const normalizedEmail = normalizeEmailValue(u.email || "");
        return (
          normalizedName.includes(nameQuery) ||
          normalizedEmail.includes(nameQuery)
        );
      })
      .slice(0, 6);
  }, [newGuardianName, users]);

  const getEffectiveUserName = (user) => {
    if (!user) return "";
    const firstName = user.firstName || user.givenName || "";
    const lastName = user.lastName || user.familyName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return String(
      user.fullName || user.name || user.username || user.email || ""
    ).trim();
  };

  /* ── "Moi" from auth ── */
  useEffect(() => {
    if (!authUser) return;

    const effectiveName = getEffectiveUserName(authUser);
    const spouseConfig = getSpouseRelationConfig(authUser);

    setMembers((prev) => ({
      ...prev,

      me: {
        ...prev.me,
        position: NODES.me,
        filled: true,

        firstName:
          authUser.firstName ||
          authUser.givenName ||
          authUser.name ||
          authUser.fullName ||
          "",

        lastName:
          authUser.lastName ||
          authUser.familyName ||
          "",

        avatarUrl: avatarSrc || null,

        gender:
          authUser.gender ||
          authUser.sex ||
          "not_specified",

        label:
          effectiveName ||
          "Moi",
      },

      /*
       * Business rule:
       * - current user male   -> only Wife
       * - current user female -> only Husband
       */
      spouse: {
        ...prev.spouse,
        label: spouseConfig.label,
        relationType: spouseConfig.relationType,
        allowedRelations: spouseConfig.allowedRelations,
      },
    }));
  }, [authUser, avatarSrc]);

  /* ── Subprofiles → parents ── */
  useEffect(() => {
    setMembers((prev) => {
      const updated = {
        ...prev,

        /*
         * Never depend on the API order:
         * parent1 is always Mother,
         * parent2 is always Father.
         */
        parent1: {
          ...INITIAL_MEMBERS.parent1,
          position: NODES.parent1,
        },

        parent2: {
          ...INITIAL_MEMBERS.parent2,
          position: NODES.parent2,
        },
      };

      if (!Array.isArray(sousProfiles) || !sousProfiles.length) {
        return updated;
      }

      const parentProfiles = sousProfiles.filter(
        (sp) => String(sp?.type || "").toLowerCase() === "parent"
      );

      parentProfiles.forEach((sp) => {
        const explicitRelation = normalizeRelationType(
          sp?.relationshipType ||
            sp?.relationType ||
            sp?.relationship ||
            sp?.relation ||
            ""
        );

        const gender = normalizeGender(sp?.gender || sp?.sex);

        let slotId = null;
        let relationType = null;

        /*
         * 1. Prefer the real relationship returned by the API.
         */
        if (explicitRelation === "mother") {
          slotId = "parent1";
          relationType = "mother";
        } else if (explicitRelation === "father") {
          slotId = "parent2";
          relationType = "father";
        }

        /*
         * 2. Fallback to gender only when the relationship is absent.
         */
        else if (gender === "female") {
          slotId = "parent1";
          relationType = "mother";
        } else if (gender === "male") {
          slotId = "parent2";
          relationType = "father";
        }

        /*
         * Do not guess when the API gives us neither relation nor gender.
         */
        if (!slotId || !relationType) {
          return;
        }

        updated[slotId] = {
          ...updated[slotId],

          filled: true,

          spId:
            sp?.id ||
            sp?._id,

          firstName:
            sp?.firstName ||
            sp?.firstname ||
            "",

          lastName:
            sp?.lastName ||
            sp?.lastname ||
            "",

          gender:
            sp?.gender ||
            sp?.sex ||
            "not_specified",

          avatarUrl:
            sp?.avatar ||
            sp?.avatarUrl ||
            sp?.profilePictureUrl ||
            null,

          relationType,
          allowedRelations: [relationType],

          label:
            `${sp?.firstName || sp?.firstname || ""} ${
              sp?.lastName || sp?.lastname || ""
            }`.trim() ||
            updated[slotId].label,
        };
      });

      return updated;
    });
  }, [sousProfiles]);

  /* ── Relatives → parents ── */
  useEffect(() => {
    if (!relativesRaw?.length) return;

    setMembers((prev) => {
      const next = { ...prev };
      let changed = false;

      const acceptedParents = relativesRaw.filter((rel) => {
        if (!isRelativeAccepted(rel)) return false;

        const relation = normalizeRelationType(
          rel?.relationshipType ||
            rel?.relationType ||
            rel?.relationship ||
            ""
        );

        return relation === "mother" || relation === "father";
      });

      acceptedParents.forEach((rel) => {
        const relation = normalizeRelationType(
          rel?.relationshipType ||
            rel?.relationType ||
            rel?.relationship ||
            ""
        );

        /*
         * Fixed business slots:
         * Mother -> top left
         * Father -> top right
         */
        const slotId =
          relation === "mother"
            ? "parent1"
            : "parent2";

        const currentSlot = next[slotId];

        /*
         * A managed sub-profile already present in the slot
         * has priority over a linked relative.
         */
        if (currentSlot?.spId) {
          return;
        }

        const relativeUserId = getRelativeUserId(rel);
        const linkedUser = extractLinkedUser(rel);

        if (linkedUser) {
          next[slotId] = {
            ...buildFilledSlot(currentSlot, linkedUser),
            relationType: relation,
            allowedRelations: [relation],
            relativeRelationId: rel?._id || rel?.id || null,
          };

          changed = true;
          return;
        }

        if (relativeUserId) {
          next[slotId] = {
            ...currentSlot,
            linkedUserId: relativeUserId,
            relationType: relation,
            allowedRelations: [relation],
            relativeRelationId: rel?._id || rel?.id || null,
          };

          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [relativesRaw]);

  /* ── Subprofiles → children ── */
  useEffect(() => {
    if (!Array.isArray(sousProfiles)) return;

    setMembers((prev) => {
      const next = { ...prev };
      let changed = false;

      const childSPs = sousProfiles.filter(
        (sp) => (sp.type || "").toLowerCase() === "child"
      );

      const allChildNodes = Object.values(next).filter(
        (m) => m.role === "child"
      );
      const groups = new Map();
      allChildNodes.forEach((m) => {
        if (!m.spId) return;
        const arr = groups.get(m.spId) || [];
        arr.push(m);
        groups.set(m.spId, arr);
      });
      groups.forEach((arr) => {
        if (arr.length <= 1) return;
        const keep = arr.find((x) => x.source === "manual") || arr[0];
        arr.forEach((x) => {
          if (x.id !== keep.id) {
            delete next[x.id];
            changed = true;
          }
        });
      });

      const nowNodes = Object.values(next).filter((m) => m.role === "child");
      const spIdToSlotId = new Map();
      nowNodes.forEach((m) => {
        if (m.spId) spIdToSlotId.set(m.spId, m.id);
      });
      const emptyManual = nowNodes.filter(
        (m) => !m.spId && m.source === "manual"
      );
      let emptyIdx = 0;

      const desiredIds = [];
      childSPs.forEach((sp) => {
        const spId = sp.id || sp._id;
        let id = spIdToSlotId.get(spId);
        if (!id) {
          id =
            emptyIdx < emptyManual.length
              ? emptyManual[emptyIdx++].id
              : `child_sp_${spId}`;
        }
        const ex = next[id];
        const updated = {
          ...(ex || {}),
          id,
          role: "child",
          label: "Enfant",
          filled: true,
          spId,
          firstName: sp.firstName,
          lastName: sp.lastName || "",
          gender: sp.gender || "not_specified",
          avatarUrl:
            sp.avatar ||
            sp.avatarUrl ||
            sp.profilePictureUrl ||
            ex?.avatarUrl ||
            null,
          source:
            ex?.source || (id.startsWith("child_sp_") ? "auto" : "manual"),
          position: ex?.position || { cx: ME_CX, cy: CHILD_CY },
        };
        if (!sameChildCore(ex, updated) || ex?.source !== updated.source) {
          next[id] = updated;
          changed = true;
        }
        desiredIds.push(id);
      });

      const leftoverEmpty = Object.values(next)
        .filter((m) => m.role === "child" && !m.spId)
        .map((m) => m.id);
      const finalOrder = Array.from(
        new Set([...desiredIds, ...leftoverEmpty])
      );

      const xs = layoutChildrenX(finalOrder.length);
      finalOrder.forEach((cid, i) => {
        const ex = next[cid];
        if (!ex) return;
        const newPos = { cx: xs[i], cy: CHILD_CY };
        if (
          !ex.position ||
          ex.position.cx !== newPos.cx ||
          ex.position.cy !== newPos.cy
        ) {
          next[cid] = { ...ex, position: newPos };
          changed = true;
        }
      });

      setChildrenIds((prevIds) =>
        arraysEqual(prevIds, finalOrder) ? prevIds : finalOrder
      );
      return changed ? next : prev;
    });
  }, [sousProfiles, members]);

  /* ── Relatives → fill slots ── */
  useEffect(() => {
    if (!relativesRaw?.length) return;
    setMembers((prev) => {
      const next = { ...prev };
      let changed = false;
      const accepted = new Map();
      relativesRaw.forEach((rel) => {
        if (!isRelativeAccepted(rel)) return;
        const uId = getRelativeUserId(rel);
        const user = extractLinkedUser(rel);
        if (uId && user) accepted.set(String(uId), user);
      });
      Object.values(next).forEach((m) => {
        if (!m || !m.linkedUserId || m.filled) return;
        const user = accepted.get(String(m.linkedUserId));
        if (!user) return;
        next[m.id] = buildFilledSlot(m, user);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [relativesRaw, siblingIds, childrenIds]);

  /* ── Relatives → sibling cards ── */
  useEffect(() => {
    const siblingRelations = relativesRaw.filter((rel) => {
      if (!isRelativeAccepted(rel)) return false;
      const relation = normalizeRelationType(
        rel?.relationshipType ||
          rel?.relationType ||
          rel?.relationship ||
          ""
      );
      return relation === "brother" || relation === "sister";
    });

    if (!siblingRelations.length) return;

    setMembers((prev) => {
      const next = { ...prev };
      const nextSiblingIds = [...siblingIds];
      let changed = false;

      siblingRelations.forEach((rel) => {
        const relativeId = getRelativeUserId(rel);
        const relationId = rel?._id || rel?.id || relativeId;
        if (!relationId) return;

        const existingId = nextSiblingIds.find((id) => {
          const member = next[id];
          return (
            member?.relativeRelationId === String(relationId) ||
            (relativeId && String(member?.linkedUserId) === String(relativeId))
          );
        });
        const id = existingId || `relative_sibling_${relationId}`;
        const linkedUser = extractLinkedUser(rel);
        const relation = normalizeRelationType(
          rel?.relationshipType ||
            rel?.relationType ||
            rel?.relationship ||
            ""
        );
        const current = next[id] || {
          id,
          role: "sibling",
          source: "relative",
          filled: false,
          position: { cx: ME_CX, cy: SIBLING_CY },
        };

        next[id] = {
          ...current,
          id,
          role: "sibling",
          source: current.source || "relative",
          relativeRelationId: String(relationId),
          linkedUserId: relativeId || current.linkedUserId || null,
          relationType: relation,
          allowedRelations: [relation],
          status: rel.status || rel.state || "pending",
          ...(linkedUser
            ? {
                ...buildFilledSlot(current, linkedUser),
                role: "sibling",
                source: current.source || "relative",
                relationType: relation,
                allowedRelations: [relation],
                status: rel.status || rel.state || "pending",
              }
            : {}),
        };

        if (!existingId) {
          nextSiblingIds.push(id);
          changed = true;
        }
      });

      if (changed) {
        const xs = layoutSiblingsX(nextSiblingIds.length);
        nextSiblingIds.forEach((id, index) => {
          if (next[id]) {
            next[id] = {
              ...next[id],
              position: { cx: xs[index], cy: SIBLING_CY },
            };
          }
        });
        setSiblingIds(nextSiblingIds);
      }

      return changed ? next : prev;
    });
  }, [relativesRaw, siblingIds]);

  /* ── Relatives → spouse card ── */
  useEffect(() => {
    const currentUserId = getUserId(authUser);
    const spouseRelation = relativesRaw.find((rel) => {
      if (!isRelativeAccepted(rel)) return false;
      const relation = normalizeRelationType(
        rel?.relationshipType ||
          rel?.relationType ||
          rel?.relationship ||
          ""
      );
      return relation === "husband" || relation === "wife";
    });

    if (!spouseRelation) return;

    const rawRelation = normalizeRelationType(
      spouseRelation.relationshipType ||
        spouseRelation.relationType ||
        spouseRelation.relationship ||
        ""
    );
    const isInitiator = String(spouseRelation.userId) === String(currentUserId);
    const relation = isInitiator
      ? rawRelation
      : rawRelation === "husband"
        ? "wife"
        : "husband";
    const linkedUser = extractLinkedUser(spouseRelation);
    const relativeId = getRelativeUserId(spouseRelation);
    if (!linkedUser && !relativeId) return;

    setMembers((prev) => {
      const current = prev.spouse || INITIAL_MEMBERS.spouse;
      if (current.spId) return prev;

      const updated = {
        ...current,
        relationType: relation,
        allowedRelations: [relation],
        linkedUserId: relativeId || current.linkedUserId || null,
        relativeRelationId:
          spouseRelation._id || spouseRelation.id || current.relativeRelationId,
        status: spouseRelation.status || spouseRelation.state || "pending",
      };

      return {
        ...prev,
        spouse: linkedUser
          ? {
              ...buildFilledSlot(updated, linkedUser),
              role: "spouse",
              relationType: relation,
              allowedRelations: [relation],
              linkedUserId: relativeId || updated.linkedUserId,
              relativeRelationId: updated.relativeRelationId,
              status: updated.status,
            }
          : updated,
      };
    });
  }, [relativesRaw, authUser]);

  /* ══════════════════════════════════════════════════════════
     ACTIVE TREE VIEW / RE-ROOTING

     ownerId      = the principal patient account (never changes)
     activeProfile = the profile currently being viewed

     Example:
     Sarra -> Mother = Hajer
     When acting as Hajer:
       Hajer becomes the center
       Sarra becomes Daughter
       Ali becomes Husband
       Hajer's own parents are new/empty parent slots
  ══════════════════════════════════════════════════════════ */

  const actingProfileId = useMemo(() => {
    return (
      actingSession?.actingAs?.id ||
      actingSession?.actingAs?._id ||
      actingSession?.actingAs ||
      actingSession?.acting_as ||
      actingSession?.profile?.id ||
      actingSession?.profile?._id ||
      actingSession?.profile?.subProfileId ||
      null
    );
  }, [actingSession]);

  const resolvedOwnerId = useMemo(() => {
    if (ownerId) return String(ownerId);

    const fromLinkedUser = Array.isArray(sousProfiles)
      ? sousProfiles
          .map((sp) => getUserId(sp?.linkedUser || sp?.owner))
          .find(Boolean)
      : null;

    if (fromLinkedUser) return String(fromLinkedUser);

    // Safe fallback only when we are NOT acting as a sub-profile.
    if (!isActingAsSubprofile) {
      const currentId = getUserId(authUser);
      return currentId ? String(currentId) : null;
    }

    return null;
  }, [ownerId, sousProfiles, isActingAsSubprofile, authUser]);

  const principalUser = useMemo(() => {
    if (resolvedOwnerId) {
      const fromUsers = users.find(
        (user) => String(getUserId(user)) === String(resolvedOwnerId)
      );

      if (fromUsers) return fromUsers;

      const fromSubProfile = Array.isArray(sousProfiles)
        ? sousProfiles
            .map((sp) => sp?.linkedUser || sp?.owner || null)
            .find(
              (user) =>
                user &&
                String(getUserId(user)) === String(resolvedOwnerId)
            )
        : null;

      if (fromSubProfile) return fromSubProfile;
    }

    return !isActingAsSubprofile ? authUser : null;
  }, [resolvedOwnerId, users, sousProfiles, isActingAsSubprofile, authUser]);

  const activeSubProfile = useMemo(() => {
    if (!isActingAsSubprofile || !actingProfileId) return null;

    const found = Array.isArray(sousProfiles)
      ? sousProfiles.find((sp) => {
          const spId = sp?.id || sp?._id || sp?.subProfileId;
          return spId && String(spId) === String(actingProfileId);
        })
      : null;

    if (found) return found;

    // Fallback: the Redux effective user can already represent the acting
    // profile even when the list has not finished reloading yet.
    return {
      id: actingProfileId,
      firstName:
        authUser?.firstName || authUser?.givenName || authUser?.name || "",
      lastName: authUser?.lastName || authUser?.familyName || "",
      gender: authUser?.gender || authUser?.sex || "not_specified",
      avatar:
        authUser?.avatar ||
        authUser?.avatarUrl ||
        authUser?.profilePictureUrl ||
        null,
      type: actingSession?.profile?.type || authUser?.type || "",
      status: actingSession?.profile?.status || authUser?.status || "active",
    };
  }, [
    isActingAsSubprofile,
    actingProfileId,
    sousProfiles,
    authUser,
    actingSession,
  ]);

  const treeView = useMemo(() => {
    // Normal account: keep the original tree exactly as before.
    if (!isActingAsSubprofile || !activeSubProfile) {
      return {
        members,
        childrenIds,
        siblingIds,
        mode: "principal",
      };
    }

    const getSpId = (sp) => sp?.id || sp?._id || sp?.subProfileId || null;

    const toName = (person, fallback = "") => {
      const firstName =
        person?.firstName || person?.firstname || person?.givenName || "";
      const lastName =
        person?.lastName || person?.lastname || person?.familyName || "";
      return `${firstName} ${lastName}`.trim() || fallback;
    };

    const toAvatar = (person) =>
      person?.avatar ||
      person?.avatarUrl ||
      person?.profilePictureUrl ||
      null;

    const childRelationFromGender = (gender) => {
      const normalized = normalizeGender(gender);
      if (normalized === "female") {
        return { type: "daughter", label: "Daughter" };
      }
      if (normalized === "male") {
        return { type: "son", label: "Son" };
      }
      return { type: "child", label: "Child" };
    };

    const siblingRelationFromGender = (gender) => {
      const normalized = normalizeGender(gender);
      if (normalized === "female") {
        return { type: "sister", label: "Sister" };
      }
      if (normalized === "male") {
        return { type: "brother", label: "Brother" };
      }
      return { type: null, label: "Brother / Sister" };
    };

    const parentRelationFromGender = (gender) => {
      const normalized = normalizeGender(gender);
      if (normalized === "female") {
        return { slot: "parent1", type: "mother", label: "Mother" };
      }
      if (normalized === "male") {
        return { slot: "parent2", type: "father", label: "Father" };
      }
      return { slot: null, type: null, label: "Parent" };
    };

    const spouseRelationFromGender = (gender) => {
      const normalized = normalizeGender(gender);
      if (normalized === "female") {
        return { type: "wife", label: "Wife" };
      }
      if (normalized === "male") {
        return { type: "husband", label: "Husband" };
      }
      return { type: "spouse", label: "Spouse" };
    };

    const buildSubProfileMember = (sp, overrides = {}) => {
      const spId = getSpId(sp);
      return {
        id: overrides.id || spId || `view_${Date.now()}`,
        filled: true,
        spId,
        firstName: sp?.firstName || sp?.firstname || "",
        lastName: sp?.lastName || sp?.lastname || "",
        gender: sp?.gender || sp?.sex || "not_specified",
        avatarUrl: toAvatar(sp),
        label: toName(sp, overrides.label || "Family member"),
        source: "reroot",
        ...overrides,
      };
    };

    const buildPrincipalMember = (overrides = {}) => {
      const firstName =
        principalUser?.firstName || principalUser?.givenName || "";
      const lastName =
        principalUser?.lastName || principalUser?.familyName || "";

      return {
        id: overrides.id || "__principal_account__",
        filled: true,
        isPrincipalAccount: true,
        principalUserId: resolvedOwnerId,
        firstName,
        lastName,
        gender: principalUser?.gender || principalUser?.sex || "not_specified",
        avatarUrl:
          principalUser?.avatar ||
          principalUser?.avatarUrl ||
          principalUser?.profilePictureUrl ||
          null,
        label:
          `${firstName} ${lastName}`.trim() ||
          principalUser?.fullName ||
          principalUser?.name ||
          "Main account",
        source: "reroot-principal",
        ...overrides,
      };
    };

    const activeId = getSpId(activeSubProfile) || actingProfileId;
    const activeType = String(activeSubProfile?.type || "").toLowerCase();
    const activeGender = normalizeGender(
      activeSubProfile?.gender || activeSubProfile?.sex
    );

    const viewMembers = {
      parent1: {
        ...INITIAL_MEMBERS.parent1,
        position: NODES.parent1,
      },
      parent2: {
        ...INITIAL_MEMBERS.parent2,
        position: NODES.parent2,
      },
      me: buildSubProfileMember(activeSubProfile, {
        id: "me",
        role: "me",
        spId: activeId,
        position: NODES.me,
        relationshipLabel: "Your profile",
      }),
      spouse: {
        ...INITIAL_MEMBERS.spouse,
        ...getSpouseRelationConfig({ gender: activeGender }),
        position: NODES.spouse,
        filled: false,
        viewOnlyWhenActing: true,
      },
    };

    let viewChildrenIds = [];
    let viewSiblingIds = [];

    const parentProfiles = Array.isArray(sousProfiles)
      ? sousProfiles.filter(
          (sp) => String(sp?.type || "").toLowerCase() === "parent"
        )
      : [];

    const childProfiles = Array.isArray(sousProfiles)
      ? sousProfiles.filter(
          (sp) => String(sp?.type || "").toLowerCase() === "child"
        )
      : [];

    /* ----------------------------------------------------------
       CASE 1 — acting as a parent

       Principal tree:
           Hajer (Mother) + Ali (Father) -> Sarra

       Acting as Hajer:
           Hajer's Mother / Father -> Hajer + Ali(Husband) -> Sarra(Daughter)
    ---------------------------------------------------------- */
    if (activeType === "parent") {
      // Parents of the active parent are NOT the principal user's parents.
      // Keep these two slots empty until those relationships are explicitly
      // registered for the active profile.
      viewMembers.parent1 = {
        ...INITIAL_MEMBERS.parent1,
        position: NODES.parent1,
        filled: false,
        viewOnlyWhenActing: true,
      };
      viewMembers.parent2 = {
        ...INITIAL_MEMBERS.parent2,
        position: NODES.parent2,
        filled: false,
        viewOnlyWhenActing: true,
      };

      // The other parent of the principal account becomes the spouse of the
      // active parent. Example: Hajer -> Ali = Husband.
      const otherParent = parentProfiles.find(
        (sp) => String(getSpId(sp)) !== String(activeId)
      );

      if (otherParent) {
        const spouseRelation = spouseRelationFromGender(
          otherParent?.gender || otherParent?.sex
        );

        viewMembers.spouse = buildSubProfileMember(otherParent, {
          id: "spouse",
          role: "spouse",
          position: NODES.spouse,
          relationType: spouseRelation.type,
          allowedRelations: [spouseRelation.type].filter(Boolean),
          relationshipLabel: spouseRelation.label,
          label: toName(otherParent, spouseRelation.label),
        });
      } else {
        const expectedSpouse =
          activeGender === "female"
            ? { label: "Husband", type: "husband" }
            : activeGender === "male"
            ? { label: "Wife", type: "wife" }
            : { label: "Spouse", type: "spouse" };

        viewMembers.spouse = {
          ...INITIAL_MEMBERS.spouse,
          position: NODES.spouse,
          filled: false,
          label: expectedSpouse.label,
          relationType: expectedSpouse.type,
          allowedRelations:
            expectedSpouse.type === "spouse"
              ? ["husband", "wife"]
              : [expectedSpouse.type],
          viewOnlyWhenActing: true,
        };
      }

      // The principal account becomes a child of the active parent.
      if (principalUser || resolvedOwnerId) {
        const childRelation = childRelationFromGender(
          principalUser?.gender || principalUser?.sex
        );
        const principalChildId = "__principal_as_child__";

        viewMembers[principalChildId] = buildPrincipalMember({
          id: principalChildId,
          role: "child",
          relationType: childRelation.type,
          allowedRelations: ["son", "daughter"],
          relationshipLabel: childRelation.label,
          position: { cx: ME_CX, cy: CHILD_CY },
        });

        viewChildrenIds.push(principalChildId);
      }

      // Every sibling of the principal account is ALSO a child of this
      // parent, so move filled sibling cards under the active parent.
      siblingIds.forEach((siblingId) => {
        const sibling = members[siblingId];
        if (!sibling?.filled) return;

        const relation = childRelationFromGender(sibling.gender);
        const childId = `__parent_child_${siblingId}`;

        viewMembers[childId] = {
          ...sibling,
          id: childId,
          role: "child",
          relationType: relation.type,
          allowedRelations: ["son", "daughter"],
          relationshipLabel: relation.label,
          source: "reroot",
        };

        viewChildrenIds.push(childId);
      });
    }

    /* ----------------------------------------------------------
       CASE 2 — acting as a child

       The principal account becomes Mother/Father.
       Other child sub-profiles become Brother/Sister.
    ---------------------------------------------------------- */
    else if (activeType === "child") {
      const ownerParentRelation = parentRelationFromGender(
        principalUser?.gender || principalUser?.sex
      );

      if (principalUser || resolvedOwnerId) {
        const targetSlot = ownerParentRelation.slot || "parent1";

        viewMembers[targetSlot] = buildPrincipalMember({
          id: targetSlot,
          role: "parent",
          relationType: ownerParentRelation.type,
          allowedRelations: ownerParentRelation.type
            ? [ownerParentRelation.type]
            : ["mother", "father"],
          relationshipLabel: ownerParentRelation.label,
          label: toName(principalUser, ownerParentRelation.label),
          position: NODES[targetSlot],
        });
      }

      // Principal user's spouse is the other parent of the active child.
      if (members.spouse?.filled) {
        const spouseAsParent = parentRelationFromGender(members.spouse.gender);
        let targetSlot = spouseAsParent.slot;

        if (!targetSlot) {
          targetSlot = ownerParentRelation.slot === "parent1"
            ? "parent2"
            : "parent1";
        }

        viewMembers[targetSlot] = {
          ...members.spouse,
          id: targetSlot,
          role: "parent",
          relationType: spouseAsParent.type,
          allowedRelations: spouseAsParent.type
            ? [spouseAsParent.type]
            : ["mother", "father"],
          relationshipLabel: spouseAsParent.label,
          position: NODES[targetSlot],
          source: "reroot",
        };
      }

      // Brothers / sisters are the other children managed by the principal
      // account.
      childProfiles
        .filter((sp) => String(getSpId(sp)) !== String(activeId))
        .forEach((sp) => {
          const relation = siblingRelationFromGender(sp?.gender || sp?.sex);
          const siblingId = `__acting_sibling_${getSpId(sp)}`;

          viewMembers[siblingId] = buildSubProfileMember(sp, {
            id: siblingId,
            role: "sibling",
            relationType: relation.type,
            allowedRelations: ["brother", "sister"],
            relationshipLabel: relation.label,
            stackBadge: relation.label,
          });

          viewSiblingIds.push(siblingId);
        });

      // A child's spouse and children are independent relationships; do not
      // reuse the principal account's spouse/children.
      viewChildrenIds = [];
    }

    /* ----------------------------------------------------------
       Unknown sub-profile type: center it, but never reuse the principal
       account's parents/children incorrectly.
    ---------------------------------------------------------- */
    else {
      viewMembers.parent1 = {
        ...INITIAL_MEMBERS.parent1,
        filled: false,
        viewOnlyWhenActing: true,
      };
      viewMembers.parent2 = {
        ...INITIAL_MEMBERS.parent2,
        filled: false,
        viewOnlyWhenActing: true,
      };
      viewMembers.spouse = {
        ...INITIAL_MEMBERS.spouse,
        ...getSpouseRelationConfig(activeSubProfile),
        filled: false,
        viewOnlyWhenActing: true,
      };
      viewChildrenIds = [];
      viewSiblingIds = [];
    }

    const childXs = layoutChildrenX(viewChildrenIds.length);
    viewChildrenIds.forEach((id, index) => {
      if (!viewMembers[id]) return;
      viewMembers[id] = {
        ...viewMembers[id],
        position: { cx: childXs[index], cy: CHILD_CY },
      };
    });

    const siblingXs = layoutSiblingsX(viewSiblingIds.length);
    viewSiblingIds.forEach((id, index) => {
      if (!viewMembers[id]) return;
      viewMembers[id] = {
        ...viewMembers[id],
        position: { cx: siblingXs[index], cy: SIBLING_CY },
      };
    });

    return {
      members: viewMembers,
      childrenIds: viewChildrenIds,
      siblingIds: viewSiblingIds,
      mode: "acting",
      activeSubProfileId: activeId,
    };
  }, [
    isActingAsSubprofile,
    activeSubProfile,
    actingProfileId,
    principalUser,
    resolvedOwnerId,
    sousProfiles,
    members,
    childrenIds,
    siblingIds,
  ]);

  /* ── Detail click handler ── */
  const handleNodeClick = (member) => {
    if (!member || member.role === "me") return;

    // The principal account can appear as Daughter/Son or Mother/Father
    // after re-rooting. It is not a sub-profile, so we never open the
    // create-subprofile modal for it. Use "Back to your account" instead.
    if (member.isPrincipalAccount) return;

    // The current backend stores sub-profiles under the principal account.
    // Do not create a fake Mother/Father/Spouse for the acting profile using
    // the principal account's ownership model. The re-rooted view is safe
    // and read-only for relationships that do not yet exist.
    if (isActingAsSubprofile && member.viewOnlyWhenActing) {
      message.info(
        "This relationship belongs to the active sub-profile. Add/edit support requires an explicit subject profile in the API."
      );
      return;
    }

    // A spouse/sibling can be a real managed sub-profile after re-rooting
    // (example: Ali becomes Husband while acting as Hajer). In that case
    // open the normal detail modal so switching to that sub-profile works.
    if (
      (member.role === "sibling" || member.role === "spouse") &&
      member.spId
    ) {
      setDetailTab("overview");
      setDetailModal(member);
      return;
    }

    if (member.role === "sibling" || member.role === "spouse") {
      if (member.relativeRelationId) return;

      if (typeof onAddRelativeFromTree === "function") {
        onAddRelativeFromTree(member, (linkInfo) => {
          if (!linkInfo?.relativeId) return;
          setMembers((prev) => {
            const slot = prev[member.id];
            if (!slot) return prev;
            let updated = { ...slot, linkedUserId: linkInfo.relativeId };
            if (linkInfo.linkedUser)
              updated = buildFilledSlot(updated, linkInfo.linkedUser);
            return { ...prev, [member.id]: updated };
          });
        });
      }
      return;
    }

    if (member.role === "parent" || member.role === "child") {
      if (member.filled && member.spId) {
        setDetailTab("overview");
        setDetailModal(member);
      } else if (member.filled) {
        // Existing linked family member without a managed sub-profile.
        setDetailTab("overview");
        setDetailModal(member);
      } else {
        setSpChoiceModal({ open: true, forMember: member });
      }
      return;
    }

    setDetailTab("overview");
    setDetailModal(member);
  };

  /* ── Action handler ── */
  const handleAction = (nodeId, actionKey) => {
    const member = members[nodeId];
    if (actionKey === "sub-profile") {
      setSubProfileModal({ open: true, forMember: member });
      return;
    }
    if (actionKey === "relative") {
      if (typeof onAddRelativeFromTree === "function") {
        onAddRelativeFromTree(member, (linkInfo) => {
          if (!linkInfo?.relativeId) return;
          setMembers((prev) => {
            const slot = prev[nodeId];
            if (!slot) return prev;
            let updated = { ...slot, linkedUserId: linkInfo.relativeId };
            if (linkInfo.linkedUser)
              updated = buildFilledSlot(updated, linkInfo.linkedUser);
            return { ...prev, [nodeId]: updated };
          });
        });
      }
      return;
    }
    setGenericModal({
      member,
      actionKey,
      actionLabel: ACTION_DEFS[actionKey]?.label,
    });
  };

  const handleSwitchClick = async (member) => {
    const subprofileId = member?.spId || member?.id;
    if (!subprofileId) {
      message.error("Subprofile id not found");
      return;
    }
    const result = await handleSwitchProfile(subprofileId);
    if (result?.ok) setDetailModal(null);
  };

  const handleRestoreClick = async () => {
    const result = await handleRestoreProfile();
    if (result?.ok) setDetailModal(null);
  };

  const getMemberDisplayName = (member) =>
    member?.firstName
      ? `${member.firstName} ${member.lastName || ""}`.trim()
      : member?.label || "this profile";

  const clearMemberFromTree = (member) => {
    const memberId = member?.id;
    if (!memberId) return;

    const spKey = String(member?.spId || memberId);
    setGuardiansBySpId((prev) => {
      if (!(spKey in prev)) return prev;
      const next = { ...prev };
      delete next[spKey];
      return next;
    });

    if (member.role === "child") {
      setChildrenIds((prev) => prev.filter((id) => id !== memberId));
    }
    if (member.role === "sibling") {
      setSiblingIds((prev) => prev.filter((id) => id !== memberId));
    }

    setMembers((prev) => {
      const current = prev[memberId];
      if (!current) return prev;

      if (INITIAL_MEMBERS[memberId]) {
        return {
          ...prev,
          [memberId]: {
            ...INITIAL_MEMBERS[memberId],
            position:
              current.position || INITIAL_MEMBERS[memberId].position,
          },
        };
      }

      const next = { ...prev };
      delete next[memberId];
      return next;
    });
  };

  const handleDeleteClick = (member) => {
    setDetailModal(null);
    setDeleteConfirmModal({ open: true, member });
  };

  const handleConfirmDelete = async () => {
    const member = deleteConfirmModal.member;
    if (!member) return;

    setIsDeletingSubprofile(true);
    try {
      if (member.relativeRelationId) {
        await dispatch(deleteRelative(member.relativeRelationId)).unwrap();
      }

      if (member.spId) {
        const result = await handleDeleteSubprofile(member.spId);
        if (!result?.ok) return;
      }

      clearMemberFromTree(member);
      setDetailModal(null);
      setDeleteConfirmModal({ open: false, member: null });
    } finally {
      setIsDeletingSubprofile(false);
    }
  };

  /* ── Sub-profile avatar upload ── */
  const handleAvatarFileChange = async (subprofileId, file) => {
    if (!subprofileId || !file) return;

    setUploadingAvatarFor(subprofileId);
    try {
      const form = new FormData();
      form.append("files", file);

      const res = await axiosGateway.post(POSTS_PATHS.UPLOAD, form, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const uploaded = Array.isArray(res.data?.data) ? res.data.data[0] : null;
      const avatarUrl = uploaded?.url;

      if (!avatarUrl) {
        message.error("Erreur téléversement de l'avatar");
        return;
      }

      const result = await handleUpdateSubprofile(subprofileId, { avatarUrl });
      if (result?.ok) {
        message.success("Photo de profil mise à jour");
        setDetailModal((prev) =>
          prev && String(prev.spId) === String(subprofileId)
            ? { ...prev, avatarUrl }
            : prev
        );
      }
    } catch {
      message.error("Erreur téléversement de l'avatar");
    } finally {
      setUploadingAvatarFor(null);
    }
  };

  /* ── Add sibling slot ── */
  const handleAddSiblingSlot = () => {
    const id = `sibling${siblingIds.length + 1}`;
    const nextIds = [...siblingIds, id];
    const xs = layoutSiblingsX(nextIds.length);

    setSiblingIds(nextIds);

    setMembers((prev) => {
      const withNew = {
        ...prev,

        [id]: {
          id,

          /*
           * Supervisor rule:
           * "Proche" is not a generic relation anymore.
           * This slot accepts only Brother or Sister.
           */
          label: "Brother / Sister",
          role: "sibling",
          relationType: null,
          allowedRelations: ["brother", "sister"],

          filled: false,
          spId: null,
          source: "manual",
          linkedUserId: null,

          position: {
            cx: xs[nextIds.length - 1],
            cy: SIBLING_CY,
          },
        },
      };

      nextIds.forEach((sid, i) => {
        if (!withNew[sid]) return;

        withNew[sid] = {
          ...withNew[sid],

          position: {
            cx: xs[i],
            cy: SIBLING_CY,
          },
        };
      });

      return withNew;
    });
  };

  /* ── Add child slot ── */
  const handleAddChildSlot = () => {
    const id = `child_slot_${Date.now()}`;
    const nextIds = [...childrenIds, id];
    const xs = layoutChildrenX(nextIds.length);
    setChildrenIds(nextIds);
    setMembers((prev) => {
      const withNew = {
        ...prev,
        [id]: {
          id,
          label: "Enfant",
          role: "child",

          /*
           * Prepared for AddRelativeForm when "link existing" is used.
           */
          relationType: null,
          allowedRelations: ["son", "daughter"],

          filled: false,
          spId: null,
          source: "manual",
          linkedUserId: null,
          position: { cx: xs[nextIds.length - 1], cy: CHILD_CY },
        },
      };
      nextIds.forEach((cid, i) => {
        if (withNew[cid]) {
          withNew[cid] = {
            ...withNew[cid],
            position: { cx: xs[i], cy: CHILD_CY },
          };
        }
      });
      return withNew;
    });
  };

  /* ══════════════════════════════════════════════════════════
     DETAIL MODAL CONTENT BUILDERS
  ══════════════════════════════════════════════════════════ */

  const renderDetailModal = () => {
    if (!detailModal) return null;
    const m = detailModal;

    const displayName = m.firstName
      ? `${m.firstName} ${m.lastName || ""}`.trim()
      : m.label;

    const initials = m.firstName
      ? `${m.firstName[0]}${m.lastName?.[0] || ""}`.toUpperCase()
      : (m.label?.[0] || "").toUpperCase();

    const spData = sousProfiles?.find((sp) => {
      const spId = sp.id || sp._id || sp.subProfileId;
      return spId && String(spId) === String(m.spId);
    });

    const rawDob =
      spData?.birthdate ||
      spData?.dateOfBirth ||
      spData?.birthDate ||
      spData?.birth_date ||
      spData?.dob ||
      spData?.date_of_birth ||
      null;

    const age = spData?.age
      ? Number(spData.age)
      : rawDob
      ? new Date().getFullYear() - new Date(rawDob).getFullYear()
      : null;

    const dobFormatted = rawDob
      ? new Date(rawDob).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : null;

    const genderRaw = (
      spData?.gender ||
      spData?.sex ||
      m.gender ||
      ""
    ).toLowerCase();
    const genderLabel =
      genderRaw === "male" || genderRaw === "homme" || genderRaw === "m"
        ? t("familyTree.detail.gender.male")
        : genderRaw === "female" ||
          genderRaw === "femme" ||
          genderRaw === "f"
        ? t("familyTree.detail.gender.female")
        : genderRaw === "other"
        ? t("familyTree.detail.gender.other")
        : null;

    const guardianObj = spData?.guardian;
    const primaryGuardianName =
      spData?.guardianName ||
      (guardianObj?.firstName
        ? `${guardianObj.firstName} ${guardianObj.lastName || ""}`.trim()
        : guardianObj?.name || null) ||
      spData?.primaryGuardian ||
      spData?.caregiver?.name ||
      (spData?.guardianFirstName
        ? `${spData.guardianFirstName} ${
            spData.guardianLastName || ""
          }`.trim()
        : null) ||
      null;

    const ownerUser = spData?.linkedUser || spData?.owner || null;
    const ownerGuardian = ownerUser
      ? {
          name:
            `${ownerUser.firstName || ""} ${ownerUser.lastName || ""}`.trim() ||
            ownerUser.fullName ||
            "Primary Guardian",
          email: ownerUser.email || "",
          userId: getUserId(ownerUser),
          type: "primary",
          status: "owner",
          permission: "write",
          canEmancipate: true,
        }
      : null;

    const guardiansList = Array.isArray(spData?.guardians)
      ? spData.guardians.map((g) => ({
          name:
            g.name ||
            g.fullName ||
            `${g.firstName || ""} ${g.lastName || ""}`.trim(),
          email: g.email || "",
          userId: g.userId || g.grantedToUserId || g.id || g._id || "",
          type: g.type || g.role || "Guardian",
        }))
      : primaryGuardianName
      ? [{ name: primaryGuardianName, type: "Primary Guardian" }]
      : ownerGuardian
      ? [ownerGuardian]
      : [];

    const guardiansCount = guardiansList.length;

    const roleLabel =
      m.relationshipLabel ||
      (m.role === "parent"
        ? t("familyTree.detail.roles.parent")
        : m.role === "child"
        ? t("familyTree.detail.roles.child")
        : m.role === "sibling"
        ? t("familyTree.detail.roles.sibling")
        : m.role === "spouse"
        ? t("familyTree.detail.roles.spouse")
        : m.role);

    const actingProfileId =
      actingSession?.actingAs?.id ||
      actingSession?.actingAs?._id ||
      actingSession?.actingAs ||
      actingSession?.profile?.id ||
      actingSession?.profile?._id ||
      actingSession?.profile?.subProfileId ||
      null;

    const memberSubprofileId = m.spId || m.id;
    const isViewingActingProfile =
      isActingAsSubprofile &&
      (!actingProfileId ||
        String(actingProfileId) === String(memberSubprofileId));

    const overviewContent = (
      <div className="ft-modal-body">
        <div className="ft-modal-grid-2">
          <div className="ft-modal-info-card">
            <div className="ft-modal-info-label">
              <UserIconOutlined className="ft-modal-info-icon" />
              {t("familyTree.detail.overview.relationship")}
            </div>
            <div className="ft-modal-info-value">{roleLabel}</div>
          </div>

          <div className="ft-modal-info-card">
            <div className="ft-modal-info-label">
              <CalendarOutlined className="ft-modal-info-icon" />
              {t("familyTree.detail.overview.age")}
            </div>
            <div className="ft-modal-info-value">
              {age ? `${age} ${t("familyTree.ui.years")}` : dobFormatted || "—"}
            </div>
          </div>

          {genderLabel && (
            <div className="ft-modal-info-card">
              <div className="ft-modal-info-label">
                <UserIconOutlined className="ft-modal-info-icon" />
                {t("familyTree.detail.overview.gender")}
              </div>
              <div className="ft-modal-info-value">{genderLabel}</div>
            </div>
          )}

          {dobFormatted && age && (
            <div className="ft-modal-info-card">
              <div className="ft-modal-info-label">
                <CalendarOutlined className="ft-modal-info-icon" />
                {t("familyTree.detail.overview.dateOfBirth")}
              </div>
              <div
                className="ft-modal-info-value"
                style={{ textTransform: "none", fontSize: 13 }}
              >
                {dobFormatted}
              </div>
            </div>
          )}
        </div>

        <div className="ft-modal-guardian-card">
          <SafetyOutlined className="ft-modal-guardian-icon" />
          <div>
            <div className="ft-modal-guardian-title">
              {t("familyTree.detail.overview.primaryGuardian")}
            </div>
            <div className="ft-modal-guardian-sub">
              {guardiansCount > 0
                ? guardiansList[0].name
                : t("familyTree.ui.primaryGuardianFallback")}
            </div>
          </div>
        </div>

        <div className="ft-modal-manage-guardian-card">
          <div className="ft-modal-manage-guardian-text">
            <div className="ft-modal-manage-guardian-title">
              {t("familyTree.guardians.title")}
            </div>
            <div className="ft-modal-manage-guardian-desc">
              {guardiansCount > 0
                ? t("familyTree.guardians.desc", {
                    name: displayName,
                  })
                : t("familyTree.ui.addGuardiansFor", { name: displayName })}
            </div>
          </div>
          <Button
            icon={<SettingOutlined />}
            className="ft-modal-manage-guardian-btn"
            onClick={() => {
              setDetailModal(null);
              setGrantIndependenceModal({ open: true, member: m });
            }}
          />
        </div>

        <div className="ft-modal-delete-card">
          <div className="ft-modal-delete-text">
            <div className="ft-modal-delete-title">
              {m.spId
                ? t("familyTree.detail.overview.deleteSubprofile")
                : t("familyTree.detail.overview.removeFromTree")}
            </div>
            <div className="ft-modal-delete-desc">
              {m.spId
                ? t("familyTree.detail.overview.deleteDesc", {
                    name: displayName,
                  })
                : t("familyTree.detail.overview.removeDesc", {
                    name: displayName,
                  })}
            </div>
          </div>
          <Button
            danger
            icon={<DeleteOutlined />}
            className="ft-modal-delete-btn"
            onClick={() => handleDeleteClick(m)}
          />
        </div>
      </div>
    );

    const spKey = String(m.spId || m.id);
    const localGuardians =
      guardiansBySpId[spKey] ??
      (guardiansList.length > 0 ? guardiansList : []);

    const handleMakePrimary = (idx) => {
      const updated = localGuardians.map((g, i) => ({
        ...g,
        type: i === idx ? "primary" : i === 0 ? "secondary" : g.type,
      }));
      const promoted = updated.splice(idx, 1)[0];
      updated.unshift(promoted);
      setGuardiansBySpId((prev) => ({ ...prev, [spKey]: updated }));
    };

    const handleRemoveGuardian = async (idx) => {
      const target = localGuardians[idx];
      if (target?.status === "owner") {
        message.warning("Primary owner access cannot be revoked here.");
        return;
      }

      const guardianUserId = target?.userId || target?.grantedToUserId;
      if (guardianUserId) {
        const result = await handleRevokeAccess(spKey, guardianUserId);
        if (!result?.ok) return;
      }
      const updated = localGuardians.filter((_, i) => i !== idx);
      setGuardiansBySpId((prev) => ({ ...prev, [spKey]: updated }));
    };

    const handleAddGuardian = async () => {
      const guardianName = newGuardianName.trim();
      const typedEmail = normalizeEmailValue(newGuardianEmail);

      const exactUser =
        selectedGuardianUser ||
        (guardianName
          ? users.find((u) => {
              const fullName =
                `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                u.fullName ||
                "";
              return (
                normalizeSearch(fullName) === normalizeSearch(guardianName) ||
                normalizeEmailValue(u.email) ===
                  normalizeEmailValue(guardianName)
              );
            })
          : null);

      const exactUserId = getUserId(exactUser);
      const guardianEmail =
        typedEmail || normalizeEmailValue(exactUser?.email);
      const guardianDisplayName =
        guardianName ||
        `${exactUser?.firstName || ""} ${exactUser?.lastName || ""}`.trim() ||
        exactUser?.fullName ||
        guardianEmail;

      if (!exactUserId && !guardianEmail) {
        message.error("Select an existing guardian or enter an email.");
        return;
      }

      const guardianType =
        localGuardians.length === 0 ? "primary" : newGuardianRole;
      const permission = guardianType === "primary" ? "write" : "read";
      const currentUserId = getUserId(authUser);
      const currentUserEmail = normalizeEmailValue(authUser?.email);
      const isCurrentOwner =
        (exactUserId &&
          currentUserId &&
          String(exactUserId) === String(currentUserId)) ||
        (guardianEmail &&
          currentUserEmail &&
          guardianEmail === currentUserEmail);

      const alreadyInList = localGuardians.some((g) => {
        const existingUserId = getUserId(g);
        const existingEmail = normalizeEmailValue(g.email);
        return (
          (exactUserId &&
            existingUserId &&
            String(exactUserId) === String(existingUserId)) ||
          (guardianEmail && existingEmail && guardianEmail === existingEmail)
        );
      });

      if (alreadyInList) {
        message.warning("Guardian already added.");
        return;
      }

      const result = isCurrentOwner
        ? { ok: true, data: { status: "owner_already_has_access" } }
        : exactUserId
        ? await handleGrantAccess(spKey, exactUserId, permission)
        : guardianEmail
        ? await handleGrantAccessByEmail(spKey, guardianEmail, permission)
        : null;

      if (!result?.ok) return;

      const invitationPending =
        String(result.data?.status || "").toLowerCase() === "invitation_sent";
      const ownerAlreadyHasAccess =
        String(result.data?.status || "").toLowerCase() ===
        "owner_already_has_access";

      const updated = [
        ...localGuardians,
        {
          name: guardianDisplayName,
          email: guardianEmail,
          userId:
            exactUserId ||
            result.data?.grantedToUserId ||
            result.data?.userId ||
            "",
          invitationId: result.data?.invitationId || null,
          status: invitationPending
            ? "pending"
            : ownerAlreadyHasAccess
            ? "owner"
            : "active",
          permission,
          type: guardianType,
          canEmancipate: guardianType === "primary",
        },
      ];

      setGuardiansBySpId((prev) => ({ ...prev, [spKey]: updated }));
      setNewGuardianName("");
      setNewGuardianEmail("");
      setNewGuardianRole("secondary");
      setSelectedGuardianUser(null);
      setAddGuardianOpen(false);
    };

    const getGuardianInitials = (name = "") =>
      name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

    const AVATAR_COLORS = [
      "#1d90d7",
      "#47586E",
      "#F6A623",
      "#E8536A",
      "#8B5CF6",
    ];

    const guardiansContent = (
      <div className="ft-modal-body">
        <div className="ft-guardian-mgmt-header">
          <div>
            <div className="ft-guardian-mgmt-title">{t("familyTree.guardians.title")}</div>
            <div className="ft-guardian-mgmt-desc">
              {t("familyTree.guardians.desc")}
            </div>
          </div>
          <button
            className="ft-guardian-add-btn"
            onClick={() => setAddGuardianOpen((v) => !v)}
          >
            {t("familyTree.guardians.addBtn")}
          </button>
        </div>

        {addGuardianOpen && (
          <div className="ft-guardian-add-form">
            <div className="ft-guardian-autocomplete">
              <input
                className="ft-guardian-input"
                placeholder={t("familyTree.ui.fullNameEmail")}
                value={newGuardianName}
                onChange={(e) => {
                  setNewGuardianName(e.target.value);
                  setSelectedGuardianUser(null);
                }}
              />

              {guardianSuggestions.length > 0 && !selectedGuardianUser ? (
                <div className="ft-guardian-suggestions">
                  {guardianSuggestions.map((u) => {
                    const fullName =
                      `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                      u.fullName ||
                      u.email;

                    return (
                      <button
                        key={u.id || u._id || u.userId || u.email}
                        type="button"
                        className="ft-guardian-suggestion"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setNewGuardianName(fullName);
                          setNewGuardianEmail(u.email || "");
                          setSelectedGuardianUser(u);
                        }}
                      >
                        <span className="ft-guardian-suggestion-name">
                          {fullName}
                        </span>
                        {u.email ? (
                          <span className="ft-guardian-suggestion-email">
                            {u.email}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : newGuardianName.trim() || newGuardianEmail.trim() ? (
                <div className="ft-guardian-no-suggestions">
                  {t("familyTree.ui.noMatch")}
                </div>
              ) : null}
            </div>

            <input
              className="ft-guardian-input"
              placeholder={t("familyTree.ui.invitationEmail")}
              value={newGuardianEmail}
              onChange={(e) => {
                setNewGuardianEmail(e.target.value);
                setSelectedGuardianUser(null);
              }}
            />

            <select
              className="ft-guardian-select"
              value={newGuardianRole}
              onChange={(e) => setNewGuardianRole(e.target.value)}
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>

            <div className="ft-guardian-add-form-actions">
              <button
                className="ft-guardian-add-confirm"
                onClick={handleAddGuardian}
              >
                {t("familyTree.guardians.form.add")}
              </button>
              <button
                className="ft-guardian-add-cancel"
                onClick={() => {
                  setNewGuardianName("");
                  setNewGuardianEmail("");
                  setNewGuardianRole("secondary");
                  setSelectedGuardianUser(null);
                  setAddGuardianOpen(false);
                }}
              >
                {t("familyTree.guardians.form.cancel")}
              </button>
            </div>
          </div>
        )}

        {localGuardians.length > 0 ? (
          localGuardians.map((g, i) => {
            const isPrimary = i === 0 || g.type === "primary";
            const avatarColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
            return (
              <div key={i} className="ft-guardian-item">
                <div className="ft-guardian-item-left">
                  <div
                    className="ft-guardian-avatar"
                    style={{ background: avatarColor }}
                  >
                    {isPrimary ? (
                      <span style={{ fontSize: 13 }}>👑</span>
                    ) : (
                      getGuardianInitials(g.name)
                    )}
                  </div>

                  <div className="ft-guardian-item-info">
                    <div className="ft-guardian-item-name-row">
                      <span className="ft-guardian-item-name">{g.name}</span>
                      <span
                        className={
                          isPrimary
                            ? "ft-guardian-badge-primary"
                            : "ft-guardian-badge-secondary"
                        }
                      >
                        {isPrimary
                          ? t("familyTree.guardians.badges.primary")
                          : t("familyTree.guardians.badges.secondary")}
                      </span>
                    </div>

                    {g.email && (
                      <div className="ft-guardian-item-email">{g.email}</div>
                    )}
                    {g.status === "pending" && (
                      <div className="ft-guardian-item-email">
                        {t("familyTree.ui.invitationPending")}
                      </div>
                    )}
                    {g.status === "owner" && (
                      <div className="ft-guardian-item-email">{t("familyTree.ui.ownerAccess")}</div>
                    )}
                    {isPrimary && (
                      <span className="ft-guardian-emancipate">
                        {t("familyTree.guardians.item.canEmancipate")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="ft-guardian-item-actions">
                  {!isPrimary && (
                    <button
                      className="ft-guardian-make-primary-btn"
                      onClick={() => handleMakePrimary(i)}
                    >
                      {t("familyTree.guardians.item.makePrimary")}
                    </button>
                  )}

                  <button
                    className="ft-guardian-remove-btn"
                    onClick={() => handleRemoveGuardian(i)}
                    title={t("familyTree.guardians.item.removeTitle")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="17" y1="8" x2="23" y2="14" />
                      <line x1="23" y1="8" x2="17" y2="14" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="ft-modal-empty">{t("familyTree.guardians.empty")}</div>
        )}

        <div className="ft-guardian-roles-box">
          <div className="ft-guardian-roles-title">🛡️ Guardian Roles:</div>
          <div className="ft-guardian-roles-item">
            <span className="ft-guardian-roles-label ft-guardian-roles-label--primary">
              Primary Guardian:
            </span>{" "}
            Full access and control, can manage other guardians
          </div>
          <div className="ft-guardian-roles-item">
            <span className="ft-guardian-roles-label ft-guardian-roles-label--secondary">
              Secondary Guardian:
            </span>{" "}
            Can view and manage medical records but cannot modify guardian
            settings
          </div>
        </div>
      </div>
    );

    return (
      <div className="ft-modal-root">
        <div className="ft-modal-header">
          <div className="ft-modal-header-inner">
            <div style={{ position: "relative", display: "inline-block" }}>
              <Avatar src={m.avatarUrl || null} size={56} className="ft-modal-avatar">
                {!m.avatarUrl && initials}
              </Avatar>
              {m.spId && (
                <label
                  htmlFor={`ft-avatar-upload-${m.spId}`}
                  title={t("familyTree.detail.changeAvatar", "Change photo")}
                  style={{
                    position: "absolute",
                    right: -4,
                    bottom: -4,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#215870",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: uploadingAvatarFor === m.spId ? "default" : "pointer",
                    border: "2px solid #fff",
                    fontSize: 11,
                  }}
                >
                  {uploadingAvatarFor === m.spId ? <LoadingOutlined /> : <CameraOutlined />}
                  <input
                    id={`ft-avatar-upload-${m.spId}`}
                    type="file"
                    accept="image/*"
                    disabled={uploadingAvatarFor === m.spId}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) handleAvatarFileChange(m.spId, file);
                    }}
                  />
                </label>
              )}
            </div>
            <div className="ft-modal-header-info">
              <div className="ft-modal-name">{displayName}</div>
              <div className="ft-modal-tags">
                {m.spId && (
                  <Tag className="ft-tag-managed">
                    {t("familyTree.detail.tags.managedSubprofile")}
                  </Tag>
                )}
                <Tag className="ft-tag-role">{roleLabel.toLowerCase()}</Tag>
                <Tag className="ft-tag-active">
                  {t("familyTree.detail.tags.active")}
                </Tag>
              </div>
            </div>
          </div>
        </div>

        <div className="ft-modal-actions">
          <Button
            type="primary"
            icon={<SwapOutlined />}
            className="ft-modal-btn-switch"
            onClick={() => handleSwitchClick(m)}
          >
            {t("familyTree.detail.switchToProfile")}
          </Button>

          {isActingAsSubprofile ? (
            <Button
              icon={<SwapOutlined />}
              className="ft-modal-btn-settings"
              onClick={handleRestoreClick}
            >
              {t("familyTree.modal.switchProfile")}
            </Button>
          ) : null}
        </div>

        {actingSession?.mode === "subprofile" &&
        actingSession?.profile?.name ? (
          <div
            style={{
              padding: "0 24px 12px",
              color: "#215870",
              fontWeight: 600,
            }}
          >
            {t("familyTree.ui.actingAs", { name: actingSession.profile.name })}
          </div>
        ) : null}

        <div className="ft-modal-tabs-wrap">
          <Segmented
            value={detailTab}
            onChange={setDetailTab}
            block
            options={[
              {
                label: t("familyTree.detail.tabs.overview"),
                value: "overview",
              },
              {
                label: `${t("familyTree.detail.tabs.guardians")}${guardiansCount > 0 ? ` (${guardiansCount})` : ""}`,
                value: "guardians",
              },
            ]}
            className="ft-modal-segmented"
          />
        </div>

        <div style={{ paddingBottom: 8 }}>
          {detailTab === "overview" ? overviewContent : guardiansContent}
        </div>
      </div>
    );
  };

  const handleGenerateAiReport = async () => {
    const subjectUserId = getUserId(authUser);

    if (!subjectUserId) {
      const msg =
        "Impossible de lancer l'analyse IA : utilisateur courant introuvable.";

      setAiReportError(msg);
      message.error(msg);
      onAiError?.(new Error(msg));
      return;
    }

    setAiReportLoading(true);
    setAiReportError("");

    // Open the Figma-style right panel immediately.
    onAiStart?.();

    try {
      const report = await generateGeneticRiskReport({
        subjectUserId,
        language: "fr",
        consentAccepted: true,
        includeSpouseFamily: true,
        includeChildren: true,
      });

      // Send the generated report to SousProfile.
      onAiGenerated?.(report);

      message.success("Rapport IA généré avec succès.");
    } catch (error) {
      const msg = getApiErrorMessage(error);

      setAiReportError(msg);
      onAiError?.(error);
      message.error(msg);
    } finally {
      setAiReportLoading(false);
    }
  };

  const handleHeaderAction = () => {
    if (aiPanelOpen) {
      onSaveReport?.();
      return;
    }

    handleGenerateAiReport();
  };

  return (
    <div
      className={[
        "ft-view",
        aiPanelOpen ? "ft-view--ai-open" : "",
        isRTL ? "ft-view--rtl" : "ft-view--ltr",
      ]
        .filter(Boolean)
        .join(" ")}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="ft-header">
        <div className="ft-header-main">
          <div>
            <h2 className="ft-title">{t("familyTree.title")}</h2>
            <p className="ft-subtitle">{t("familyTree.subtitle")}</p>
          </div>

          <Button
            type="primary"
            className={[
              "ft-ai-generate-btn",
              aiPanelOpen ? "ft-ai-generate-btn--save" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            icon={
              aiPanelOpen ? (
                <SaveOutlined />
              ) : (
                <GenerateAiIcon size={16} />
              )
            }
            loading={!aiPanelOpen && aiReportLoading}
            disabled={
              aiPanelOpen && (!canSaveReport || aiReportLoading)
            }
            onClick={handleHeaderAction}
          >
            {aiPanelOpen ? t("familyTree.ui.saveReport") : t("familyTree.ui.generateAi")}
          </Button>
        </div>

        {aiReportError && <div className="ft-ai-error">{aiReportError}</div>}
      </div>

      <FamilyTreeBoard
        members={treeView.members}
        childrenIds={treeView.childrenIds}
        siblingIds={treeView.siblingIds}
        onNodeClick={handleNodeClick}
        onSiblingMore={handleDeleteClick}
        onAddSibling={
          treeView.mode === "acting"
            ? () =>
                message.info(
                  "Add relative from an acting sub-profile requires API support for an explicit subject profile."
                )
            : handleAddSiblingSlot
        }
        onAddChild={
          treeView.mode === "acting"
            ? () =>
                message.info(
                  "Add child from an acting sub-profile requires API support for an explicit subject profile."
                )
            : handleAddChildSlot
        }
        compact={aiPanelOpen}
      />

      <Modal
        open={subProfileModal.open}
        onCancel={() => setSubProfileModal({ open: false, forMember: null })}
        footer={null}
        centered
        width={520}
        styles={{
          header: {
            background: isDark ? "#1f2937" : "#fff",
            borderBottom: `1px solid ${isDark ? "#1f2937" : "#EEF3F7"}`,
            padding: "18px 24px 12px",
          },
          content: {
            borderRadius: 20,
            overflow: "hidden",
            paddingTop: 12,
            background: isDark ? "#1f2937" : "#fff",
          },
        }}
        title={
          <span
            style={{
              color: isDark ? "#f9fafb" : "#1A2E3D",
              fontWeight: 600,
              fontSize: 17,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {t("familyTree.modal.createSubprofile")}
          </span>
        }
      >
        <SubProfileForm
          parentMember={subProfileModal.forMember}
          onCancel={() =>
            setSubProfileModal({ open: false, forMember: null })
          }
          onCreated={(sp) => {
            const slotId = subProfileModal.forMember?.id;
            const memberRole = subProfileModal.forMember?.role;
            if (slotId) {
              setMembers((prev) => ({
                ...prev,
                [slotId]: {
                  ...prev[slotId],
                  source: prev[slotId]?.source || "manual",
                  filled: true,
                  spId: sp.id || sp._id,
                  firstName: sp.firstName,
                  lastName: sp.lastName || "",
                  gender: sp.gender || "not_specified",
                  label: `${sp.firstName} ${sp.lastName || ""}`,
                },
              }));
            }
            if (memberRole === "child") {
              setChildrenIds((prev) =>
                slotId && !prev.includes(slotId) ? [...prev, slotId] : prev
              );
            } else if (memberRole === "sibling") {
              setSiblingIds((prev) =>
                slotId && !prev.includes(slotId) ? [...prev, slotId] : prev
              );
            }
            setSubProfileModal({ open: false, forMember: null });
            setDetailTab("overview");
            setDetailModal({
              ...subProfileModal.forMember,
              filled: true,
              spId: sp.id || sp._id,
              firstName: sp.firstName,
              lastName: sp.lastName || "",
              gender: sp.gender || "not_specified",
              label: `${sp.firstName} ${sp.lastName || ""}`.trim(),
            });
          }}
        />
      </Modal>

      <Modal
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        centered
        width={500}
        styles={{
          content: { borderRadius: 16, padding: 0, overflow: "hidden" },
          body: { overflowY: "hidden", overflowX: "hidden" },
        }}
      >
        {renderDetailModal()}
      </Modal>

      <Modal
        open={deleteConfirmModal.open}
        onCancel={() =>
          setDeleteConfirmModal({ open: false, member: null })
        }
        onOk={handleConfirmDelete}
        confirmLoading={isDeletingSubprofile}
        okText={t("familyTree.modal.confirm", { defaultValue: "Confirm" })}
        cancelText={t("familyTree.modal.cancel", { defaultValue: "Cancel" })}
        okButtonProps={{ danger: true }}
        centered
        zIndex={2100}
        title={
          deleteConfirmModal.member?.spId
            ? t("familyTree.overview.deleteSubprofile", {
                defaultValue: "Delete Subprofile",
              })
            : t("familyTree.overview.removeFromFamily", {
                defaultValue: "Remove from Family Tree",
              })
        }
      >
        <p style={{ margin: 0, color: isDark ? "#e5e7eb" : "#475569" }}>
          {deleteConfirmModal.member?.spId
            ? t("familyTree.overview.deleteSubprofileDesc", {
                name: getMemberDisplayName(deleteConfirmModal.member),
                defaultValue: `Permanently delete ${getMemberDisplayName(
                  deleteConfirmModal.member
                )}'s medical profile. This action cannot be undone.`,
              })
            : t("familyTree.overview.removeFromFamilyDesc", {
                name: getMemberDisplayName(deleteConfirmModal.member),
                defaultValue: `Remove ${getMemberDisplayName(
                  deleteConfirmModal.member
                )} from your family tree. This action cannot be undone.`,
              })}
        </p>
      </Modal>

      <Modal
        open={spChoiceModal.open}
        onCancel={() => setSpChoiceModal({ open: false, forMember: null })}
        footer={null}
        centered
        width={380}
        styles={{
          content: {
            borderRadius: 20,
            overflow: "hidden",
            padding: "28px 24px 24px",
          },
        }}
        title={
          <span
            style={{
              color: isDark ? "#f9fafb" : "#2E405C",
              fontWeight: 700,
              fontSize: 16,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {spChoiceModal.forMember?.role === "parent"
              ? t("familyTree.modal.addParent")
              : t("familyTree.modal.addChild")}
          </span>
        }
      >
        <p
          style={{
            color: isDark ? "#9CA3AF" : "#7A889F",
            fontSize: 13,
            fontFamily: "Poppins, sans-serif",
            margin: "0 0 16px",
          }}
        >
          {t("familyTree.modal.description")}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button
            type="primary"
            block
            size="large"
            icon={<UserAddOutlined />}
            style={{
              borderRadius: 10,
              height: 50,
              fontWeight: 600,
              fontFamily: "Poppins, sans-serif",
              background: "#1d90d7",
              borderColor: "#1d90d7",
            }}
            onClick={() => {
              setSpChoiceModal({ open: false, forMember: null });
              setSubProfileModal({
                open: true,
                forMember: spChoiceModal.forMember,
              });
            }}
          >
            {t("familyTree.modal.createNew")}
          </Button>

          <Button
            block
            size="large"
            icon={<TeamOutlined />}
            style={{
              borderRadius: 10,
              height: 50,
              fontWeight: 600,
              fontFamily: "Poppins, sans-serif",
              borderColor: isDark ? "#1f3a5f" : "#D7E6FF",
              color: "#1d90d7",
              background: isDark ? "#0c1f2e" : "#F5F9FF",
            }}
            onClick={() => {
              const member = spChoiceModal.forMember;
              setSpChoiceModal({ open: false, forMember: null });
              if (typeof onAddRelativeFromTree === "function") {
                onAddRelativeFromTree(member, (linkInfo) => {
                  if (!linkInfo?.relativeId) return;
                  setMembers((prev) => {
                    const slot = prev[member.id];
                    if (!slot) return prev;
                    let updated = {
                      ...slot,
                      linkedUserId: linkInfo.relativeId,
                    };
                    if (linkInfo.linkedUser)
                      updated = buildFilledSlot(
                        updated,
                        linkInfo.linkedUser
                      );
                    return { ...prev, [member.id]: updated };
                  });
                });
              }
            }}
          >
            {t("familyTree.modal.linkExisting")}
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!genericModal}
        onCancel={() => setGenericModal(null)}
        footer={null}
        centered
        styles={{
          header: {
            background: isDark ? "#1f2937" : "#fff",
            borderBottom: `1px solid ${isDark ? "#1f2937" : "#EEF3F7"}`,
          },
          content: {
            borderRadius: 20,
            overflow: "hidden",
            background: isDark ? "#1f2937" : "#fff",
          },
        }}
        title={
          <span
            style={{
              color: isDark ? "#f9fafb" : "#1A2E3D",
              fontWeight: 600,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {genericModal?.actionLabel}
          </span>
        }
      >
        {genericModal && (
          <div className="ft-generic-modal-body">
            <Avatar
              size={56}
              icon={<UserOutlined />}
              style={{ background: "#5B8BF0" }}
            />
            <p className="ft-generic-modal-text">
              {t("familyTree.modal.genericConfirm1")} {" "}
              <strong>{genericModal.actionLabel?.toLowerCase()}</strong>{" "}
              {t("familyTree.modal.genericConfirm2")} {" "}
              <strong>{genericModal.member?.label}</strong>.
            </p>
            <div className="ft-generic-modal-actions">
              <Button type="primary" block className="ft-generic-modal-confirm">
                {t("familyTree.modal.confirm")}
              </Button>
              <Button
                block
                onClick={() => setGenericModal(null)}
                style={{ marginTop: 8 }}
              >
                {t("familyTree.grantIndependence.cancel")}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={primaryGuardianPopup.open}
        onCancel={() =>
          setPrimaryGuardianPopup({ open: false, member: null })
        }
        footer={null}
        centered
        width={400}
        styles={{
          content: { borderRadius: 16, padding: 0, overflow: "hidden" },
          body: { padding: "24px" },
        }}
        title={
          <span
            style={{
              color: isDark ? "#f9fafb" : "#2E405C",
              fontWeight: 700,
              fontSize: 16,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {t("familyTree.overview.primaryGuardian")}
          </span>
        }
      >
        {primaryGuardianPopup.member &&
          (() => {
            const m = primaryGuardianPopup.member;
            const guardiansData =
              guardiansBySpId[String(m.spId || m.id)] || [];
            const primaryGuardian = guardiansData[0];

            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {primaryGuardian ? (
                  <div
                    style={{
                      padding: "12px",
                      borderRadius: "8px",
                      backgroundColor: isDark ? "#1f2937" : "#F5F9FF",
                      border: `1px solid ${
                        isDark ? "#374151" : "#D7E6FF"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: 12,
                        color: isDark ? "#9CA3AF" : "#7A889F",
                      }}
                    >
                      {t("familyTree.guardianManagement.guardianName")}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 600,
                        color: isDark ? "#f9fafb" : "#2E405C",
                      }}
                    >
                      {primaryGuardian.name}
                    </p>
                  </div>
                ) : (
                  <p
                    style={{
                      color: isDark ? "#9CA3AF" : "#7A889F",
                      fontSize: 13,
                      textAlign: "center",
                    }}
                  >
                    {t("familyTree.guardianManagement.noPrimaryGuardian")}
                  </p>
                )}

                <Button
                  type="primary"
                  block
                  style={{
                    borderRadius: "8px",
                    height: "40px",
                    fontWeight: "600",
                    backgroundColor: "#1d90d7",
                    borderColor: "#1d90d7",
                  }}
                  onClick={() => {
                    setGrantIndependenceModal({
                      open: true,
                      member: primaryGuardianPopup.member,
                    });
                    setPrimaryGuardianPopup({ open: false, member: null });
                  }}
                >
                  {t("familyTree.guardianManagement.editGuardian")}
                </Button>
              </div>
            );
          })()}
      </Modal>

      <Modal
        open={grantIndependenceModal.open}
        onCancel={() => {
          setGrantIndependenceModal({ open: false, member: null });
          setGrantConfirmText("");
          setEmailError("");
          setSubmitError("");
        }}
        footer={null}
        centered
        width={520}
        wrapClassName="ft-grant-independence-modal-wrapper"
        styles={{
          content: {
            borderRadius: 12,
            padding: 0,
            overflow: "hidden",
            maxHeight: "90vh",
          },
          body: {
            padding: 0,
            overflow: "auto",
            maxHeight: "calc(90vh - 60px)",
            scrollbarWidth: "none",
          },
        }}
      >
        {grantIndependenceModal.member &&
          (() => {
            const m = grantIndependenceModal.member;

            const spData = sousProfiles?.find((sp) => {
              const spId = sp.id || sp._id || sp.subProfileId;
              return spId && String(spId) === String(m.spId);
            });

            const guardianObj = spData?.guardian;
            const guardiansList = Array.isArray(spData?.guardians)
              ? spData.guardians.map((g) => ({
                  name:
                    g.name ||
                    g.fullName ||
                    `${g.firstName || ""} ${g.lastName || ""}`.trim(),
                  email: g.email || "",
                  userId:
                    g.userId || g.grantedToUserId || g.id || g._id || "",
                  type: g.type || g.role || "Guardian",
                }))
              : spData?.guardian || spData?.linkedUser
              ? [
                  {
                    name:
                      spData?.guardianName ||
                      (guardianObj?.firstName
                        ? `${guardianObj.firstName} ${
                            guardianObj.lastName || ""
                          }`.trim()
                        : guardianObj?.name) ||
                      (spData?.linkedUser?.firstName
                        ? `${spData.linkedUser.firstName} ${
                            spData.linkedUser.lastName || ""
                          }`.trim()
                        : "Primary Guardian"),
                    email:
                      spData?.linkedUser?.email || guardianObj?.email || "",
                    type: "primary",
                  },
                ]
              : [];

            const spKey = String(m.spId || m.id);
            const guardiansData = guardiansBySpId[spKey] ?? guardiansList;
            const primaryGuardian = guardiansData[0];

            return (
              <div className="ft-grant-independence-container">
                <h2 className="ft-grant-independence-modal-title">
                  {t("familyTree.grantIndependence.title")}
                </h2>

                <div className="ft-grant-independence-warning">
                  <span className="ft-grant-independence-warning-icon">⚠</span>
                  <div>
                    <p className="ft-grant-independence-warning-title">
                      {t("familyTree.grantIndependence.warning")}
                    </p>
                    <p className="ft-grant-independence-warning-text">
                      {t("familyTree.grantIndependence.warningText", {
                        name: m.label || primaryGuardian?.name,
                      })}
                    </p>
                  </div>
                </div>

                {emailError && (
                  <div className="ft-grant-independence-error-alert">
                    <span className="ft-grant-independence-error-icon">🚫</span>
                    <div style={{ flex: 1 }}>
                      <p className="ft-grant-independence-error-title">
                        Email already registered
                      </p>
                      <p className="ft-grant-independence-error-message">
                        {emailError}
                      </p>
                    </div>
                  </div>
                )}

                {submitError && (
                  <div className="ft-grant-independence-error-alert">
                    <span className="ft-grant-independence-error-icon">❌</span>
                    <div style={{ flex: 1 }}>
                      <p className="ft-grant-independence-error-title">
                        {t("familyTree.grantIndependence.errorSendingEmail") ||
                          "Error sending email"}
                      </p>
                      <p className="ft-grant-independence-error-message">
                        {submitError}
                      </p>
                    </div>
                  </div>
                )}

                <div className="ft-grant-independence-section">
                  <label className="ft-grant-independence-label">
                    {t("familyTree.grantIndependence.childEmail")}
                  </label>
                  <p className="ft-grant-independence-description">
                    {t("familyTree.grantIndependence.emailDescription")}
                  </p>
                  <input
                    type="email"
                    placeholder="email@example.com"
                    value={editableEmail}
                    onChange={(e) => setEditableEmail(e.target.value)}
                    className="ft-grant-independence-input"
                    style={
                      emailError
                        ? {
                            borderColor: "#dc2626",
                            backgroundColor: "#fee2e2",
                          }
                        : {}
                    }
                  />

                  {emailError && (
                    <div className="ft-grant-inline-error">
                      <span>⚠️</span>
                      <span>{emailError}</span>
                    </div>
                  )}
                </div>

                <div className="ft-grant-independence-section">
                  <label className="ft-grant-independence-label">
                    {t("familyTree.grantIndependence.confirmationLabel")}
                  </label>
                  <input
                    type="text"
                    placeholder="CONFIRM"
                    value={grantConfirmText}
                    onChange={(e) => setGrantConfirmText(e.target.value)}
                    className="ft-grant-independence-input"
                  />
                </div>

                <div className="ft-grant-independence-section ft-grant-independence-sticky">
                  <label className="ft-grant-independence-label">
                    {t("familyTree.grantIndependence.manageGuardians")}
                  </label>

                  <div className="ft-grant-independence-guardians-list">
                    {guardiansData && guardiansData.length > 0 ? (
                      guardiansData.map((guardian, idx) => (
                        <div
                          key={idx}
                          className="ft-grant-independence-guardian-item"
                        >
                          <div className="ft-grant-independence-guardian-info">
                            <p className="ft-grant-independence-guardian-name">
                              {guardian.name || "Unknown"}
                            </p>
                            <p className="ft-grant-independence-guardian-email">
                              {guardian.email || "No email"}
                            </p>
                          </div>
                          <span className="ft-grant-independence-guardian-type">
                            {guardian.type || "Guardian"}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="ft-grant-independence-no-guardians">
                        {t("familyTree.grantIndependence.manageGuardians") ===
                        "Manage Guardians"
                          ? "No guardians assigned"
                          : "Aucun tuteur assigné"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="ft-grant-independence-info-box">
                  <p className="ft-grant-independence-info-title">
                    {t("familyTree.grantIndependence.nextSteps")}
                  </p>
                  <ol className="ft-grant-independence-info-list">
                    <li>{t("familyTree.grantIndependence.step1")}</li>
                    <li>
                      {t("familyTree.grantIndependence.step2", {
                        name: m.label || primaryGuardian?.name,
                      })}
                    </li>
                    <li>{t("familyTree.grantIndependence.step3")}</li>
                    <li>{t("familyTree.grantIndependence.step4")}</li>
                  </ol>
                </div>

                <div className="ft-grant-independence-actions">
                  <button
                    className="ft-grant-independence-btn-cancel"
                    onClick={() => {
                      setGrantIndependenceModal({ open: false, member: null });
                      setGrantConfirmText("");
                      setEmailError("");
                      setSubmitError("");
                      setEditableEmail("");
                    }}
                  >
                    {t("familyTree.grantIndependence.cancel")}
                  </button>

                  <button
                    className="ft-grant-independence-btn-submit"
                    disabled={grantConfirmText !== "CONFIRM" || !!emailError}
                    onClick={async () => {
                      setSubmitError("");

                      const spId = m.spId || m.id;
                      const email = editableEmail;

                      if (!email) {
                        message.error(
                          t("familyTree.grantIndependence.errorNoEmail")
                        );
                        return;
                      }

                      if (emailError) {
                        message.error(
                          t(
                            "familyTree.grantIndependence.emailAlreadyExists",
                            { email }
                          )
                        );
                        return;
                      }

                      try {
                        await dispatch(
                          sendEmancipationEmail({ id: spId, email })
                        ).unwrap();

                        message.success(
                          t("familyTree.grantIndependence.successMessage")
                        );
                        setGrantIndependenceModal({
                          open: false,
                          member: null,
                        });
                        setGrantConfirmText("");
                        refetch();
                      } catch (error) {
                        console.error("Emancipation error:", error);
                        const errorMsg =
                          error?.message ||
                          error ||
                          t("familyTree.grantIndependence.errorFailed");
                        setSubmitError(errorMsg);
                      }
                    }}
                  >
                    {t("familyTree.grantIndependence.submit")}
                  </button>
                </div>
              </div>
            );
          })()}
      </Modal>
    </div>
  );
}
